export interface GameEventParticipantDto {
	number: number;
	participantName: string;
}

export interface GameEventDto {
	categoryId: number;
	categoryName: string;
	categorySortOrder: number;
	counter: number;
	eventCodeId?: number | null;
	eventId: number;
	eventName: string;
	eventStart: number;
	gamesCount: number;
	infos: string;
	liveMatchStatus: string;
	liveMatchStatusType: number;
	parentCategoryCountryCode?: string | null;
	parentId: number;
	parentName: string;
	parentSortOrder: number;
	partialScores?: string | null;
	participants?: GameEventParticipantDto[] | null;
	providerId?: number | null;
	remoteId?: number | null;
	remoteStatus?: number | null;
	result: string;
	score: string;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
	time: number;
	treatAsSport?: number | null;
	extras?: Record<string, any>;
	finished?: boolean | null;
	isLive?: boolean | null;
	servingTeam?: number | null;
	/** Present only for football games */
	red?: string | null;
	/** Present only for football games */
	yellow?: string | null;
	/** Present only for football games */
	corners?: string | null;
	eventRemoteId?: string | null;
	periodTimeLeft?: string | null;
}

export interface RunningGamesResponseSportsDto {
	events: Record<string, GameEventDto>;
	/** Games grouped by their type */
	gameTypes: Record<string, GameWithEventDataDto[]>;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
}

export interface RunningGamesResponseDto {
	/** Present only if getGameTypeCodes is set to true */
	gameTypeCodes?: Record<string, (number | number[])[]>;
	sports: RunningGamesResponseSportsDto[];
}

export interface RefreshOddsResponseDataDto {
	active: boolean;
	outcomeId: number;
	outcomeOdds?: number | null;
}

export interface RefreshOddsResponseDto {
	odds: RefreshOddsResponseDataDto[];
}

export interface GameOutcomeDto {
	outcomeId: number;
	outcomeName: string;
	outcomeOdds?: number | null;
	status?: number | null;
}

export interface GameWithEventDataDto {
	categoryBuilder?: number | null;
	combinationType: number;
	eventBuilder: number;
	gameId: number;
	gameName: string;
	gameType: number;
	mainGame: boolean;
	marketType: number;
	outcomes: GameOutcomeDto[];
	sortOrder: number;
	status: number;
	argument?: number | null;
	gameCodeId?: number | null;
	marketTypes?: number[] | null;
	eventId: number;
	combinationTypes?: number[] | null;
}

export interface RunningEventsResponseSportDto {
	events: GameEventDto[];
	/** Games grouped by their type */
	gameTypes: Record<string, GameWithEventDataDto[]>;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
}

export interface RunningEventsResponseDto {
	sports: RunningEventsResponseSportDto[];
	/** Present only if getGameTypeCodes is set to true */
	gameTypeCodes?: Record<string, (number | number[])[]>;
}

export interface RunningEventsBySportResponseSportDto {
	events: GameEventDto[];
	/** Games grouped by their type */
	gameTypes: Record<string, GameWithEventDataDto[]>;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
}

export interface RunningEventsBySportResponseDto {
	gameTypeCodes: Record<string, (number | number[])[]>;
	sports: RunningEventsBySportResponseSportDto[];
}

export interface EsportGameDto {
	gameId: number;
	gameName: string;
	gameType: number;
	combinationType: number;
	mainGame: boolean;
	status: number;
	gameCodeId?: number | null;
	sortOrder: number;
	outcomes: GameOutcomeDto[];
	marketType: number;
	marketTypes?: number[] | null;
	eventBuilder: number;
	categoryBuilder: number;
	argument?: number | null;
}

export interface EsportGameEventDto {
	categoryId: number;
	categoryName: string;
	categorySortOrder: number;
	counter: number;
	eventCodeId?: number | null;
	eventId: number;
	eventName: string;
	eventStart: number;
	gamesCount: number;
	infos: string;
	liveMatchStatus: string;
	liveMatchStatusType: number;
	parentCategoryCountryCode?: string | null;
	parentId: number;
	parentName: string;
	parentSortOrder: number;
	partialScores?: string | null;
	participants?: GameEventParticipantDto[] | null;
	providerId?: number | null;
	remoteId?: number | null;
	remoteStatus?: number | null;
	result: string;
	score: string;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
	time: number;
	treatAsSport?: number | null;
	extras?: Record<string, any>;
	finished?: boolean | null;
	isLive?: boolean | null;
	servingTeam?: number | null;
	eventRemoteId?: string | null;
	periodTimeLeft?: string | null;
	games?: EsportGameDto[] | null;
}

export interface RunningEsportEventsResponseEsportDto {
	events: EsportGameEventDto[];
	sportId: number;
	sportName: string;
	sportSortOrder: number;
}

export interface RunningEsportEventsResponseDto {
	esports: RunningEsportEventsResponseEsportDto[];
}

export interface MarketTypeDto {
	marketType: number;
	gameIds: number[];
}

export interface RunningEsportEventsByIdResponseEventDto {
	categoryId: number;
	categoryName: string;
	categorySortOrder: number;
	counter: number;
	eventCodeId?: number | null;
	eventId: number;
	eventName: string;
	eventStart: number;
	gamesCount: number;
	infos: string;
	liveMatchStatus: string;
	liveMatchStatusType: number;
	parentCategoryCountryCode?: string | null;
	parentId: number;
	parentName: string;
	parentSortOrder: number;
	partialScores?: string | null;
	participants?: GameEventParticipantDto[] | null;
	providerId?: number | null;
	remoteId?: number | null;
	remoteStatus?: number | null;
	result: string;
	score: string;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
	time: number;
	treatAsSport?: number | null;
	extras?: Record<string, any>;
	finished?: boolean | null;
	isLive?: boolean | null;
	servingTeam?: number | null;
	eventRemoteId?: string | null;
	periodTimeLeft?: string | null;
	games?: EsportGameDto[] | null;
	marketTypes?: MarketTypeDto[] | null;
}

export interface RunningEsportEventsByIdResponseDto {
	events: RunningEsportEventsByIdResponseEventDto[];
}

export interface GroupedRunningEsportEventsResponseGameDto {
	gameId: number;
	gameName: string;
	gameType: number;
	combinationType: number;
	mainGame?: boolean | null;
	status?: number | null;
	gameCodeId?: number | null;
	sortOrder?: number | null;
	outcomes: GameOutcomeDto[];
	marketType?: number | null;
	marketTypes?: number[] | null;
	eventBuilder?: number | null;
	categoryBuilder?: number | null;
	argument?: number | null;
	gameCode?: number | null;
	periodId?: number | null;
	gameLayout?: number | null;
	eventLayout?: number | null;
	combinationTypes?: number[] | null;
}

export interface GroupedRunningEsportEventsResponseEventExtendedDataDto {
	neutralGround?: string | null;
	remoteCategoryId?: string | null;
	liveEventId?: number | null;
}

export interface GroupedRunningEsportEventsResponseEventDto {
	eventId?: number | null;
	sportSortOrder?: number | null;
	remoteId?: number | null;
	eventStart: number;
	score?: string | null;
	result?: string | null;
	remoteStatus?: number | null;
	sportId: number;
	categoryId: number;
	parentId: number;
	sportName: string;
	categoryName: string;
	parentName: string;
	categorySortOrder?: number | null;
	parentSortOrder?: number | null;
	time?: number | null;
	eventCodeId?: number | null;
	eventName: string;
	partialScores?: string | null;
	participants?: GameEventParticipantDto[] | null;
	infos?: string | null;
	games?: GroupedRunningEsportEventsResponseGameDto[] | null;
	gamesCount: number;
	counter?: number | null;
	treatAsSport?: number | null;
	liveMatchStatus?: string | null;
	liveMatchStatusType?: number | null;
	parentCategoryCountryCode?: string | null;
	providerId?: number | null;
	eventRemoteId?: string | null;
	isLive?: boolean | null;
	extras?: Record<string, any>;
	videoStream?: boolean | null;
	eventExtendedData?: GroupedRunningEsportEventsResponseEventExtendedDataDto | null;
	eventType?: number | null;
	marketTypes?: number[] | null | MarketTypeDto[] | null | (number[] | (null & MarketTypeDto[]) | null);
	servingTeam?: number | null;
}

export interface GroupedRunningEsportEventsResponseEsportDto {
	events: GroupedRunningEsportEventsResponseEventDto[];
	sportId: number;
	sportName: string;
	sportSortOrder?: number | null;
}

export interface GroupedRunningEsportEventsResponseDto {
	esports: GroupedRunningEsportEventsResponseEsportDto[];
}

export interface RunningEventsByIdResponseGameDto {
	categoryBuilder?: number | null;
	combinationType: number;
	eventBuilder: number;
	gameId: number;
	gameName: string;
	gameType: number;
	mainGame: boolean;
	marketType: number;
	outcomes: GameOutcomeDto[];
	sortOrder: number;
	status: number;
	argument?: number | null;
	gameCodeId?: number | null;
	marketTypes?: number[] | null;
	combinationTypes?: number[] | null;
	isCashoutAvailable: boolean;
}

export interface RunningEventsByIdResponseEventDto {
	categoryId: number;
	categoryName: string;
	categorySortOrder: number;
	counter: number;
	eventCodeId?: number | null;
	eventId: number;
	eventName: string;
	eventStart: number;
	gamesCount: number;
	infos: string;
	liveMatchStatus: string;
	liveMatchStatusType: number;
	parentCategoryCountryCode?: string | null;
	parentId: number;
	parentName: string;
	parentSortOrder: number;
	partialScores?: string | null;
	participants?: GameEventParticipantDto[] | null;
	providerId?: number | null;
	remoteId?: number | null;
	remoteStatus?: number | null;
	result: string;
	score: string;
	sportId: number;
	sportName: string;
	sportSortOrder: number;
	time: number;
	treatAsSport?: number | null;
	extras?: Record<string, any>;
	finished?: boolean | null;
	isLive?: boolean | null;
	servingTeam?: number | null;
	/** Present only for football games */
	red?: string | null;
	/** Present only for football games */
	yellow?: string | null;
	/** Present only for football games */
	corners?: string | null;
	eventRemoteId?: string | null;
	periodTimeLeft?: string | null;
	games?: RunningEventsByIdResponseGameDto[] | null;
	marketTypes?: MarketTypeDto[] | null;
	customBets: number[];
}

export interface RunningEventsByIdResponseDto {
	events: RunningEventsByIdResponseEventDto[];
}

export interface RunningSportCategoriesResponseSportDto {
	categoryFlag?: string | null;
	categoryId: number;
	categoryName: string;
	eventsCount: number;
	level: number;
	parentCategory: number;
	parentName?: string | null;
	remoteId?: number | null;
	sortOrder: number;
	sportId: number;
	sportName?: string | null;
	treatAsSport?: number | null;
}

export interface RunningSportCategoriesResponseDto {
	sports: RunningSportCategoriesResponseSportDto[];
	gameTypeCodes: Record<string, (number | number[])[]>;
}

export interface RunningEventsDropdownResponseEventInfoDto {
	info: string;
	team?: number | null;
	time?: number | null;
	type: string;
}

export interface RunningEventsDropdownResponseEventDto {
	eventId: number;
	eventName: string;
	eventStart: number;
	infos?: RunningEventsDropdownResponseEventInfoDto[] | null;
	result: string;
	time: number;
	participants?: GameEventParticipantDto[] | null;
}

export interface RunningEventsDropdownResponseDto {
	events: RunningEventsDropdownResponseEventDto[];
}

export interface RunningEventCountResponseCountDto {
	eventsCount: number;
	sortOrder: number;
	sportId: number;
	sportName: string;
}

export interface RunningEventCountResponseDto {
	eventsCount: RunningEventCountResponseCountDto[];
}

export interface GameOutcome {
	outcomeId: number;
	outcomeOdds: number;
}

export interface GetGameOutcomeResponseDTO {
	outcomes: GameOutcome[];
}

export interface BestsellerEventsResponseEventGameDto {
	argument: number | null;
	combinationType: number | null;
	combinationTypes?: number[] | null;
	gameCode: number | null;
	gameId: number | null;
	gameName: string | null;
	gameType: number | null;
	marketTypes: number[] | null;
	outcomes: GameOutcomeDto[] | null;
}

export interface BestsellerEventsResponseEventDto {
	categoryId: number;
	category3AggregatedId?: number | null;
	categoryName: string;
	eventCodeId: number;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	games: BestsellerEventsResponseEventGameDto[];
	gamesCount: number;
	marketTypes: MarketTypeDto[];
	parentId: number;
	parentName: string;
	participants: GameEventParticipantDto[];
	remoteId: number;
	sportId: number;
	sportName: string;
	treatAsSport: number;
	eventExtendedData?: Record<string, any>;
	isLive: boolean | null;
	result?: string | null;
}

export interface BestsellerEventsResponseSportDto {
	events: BestsellerEventsResponseEventDto[];
	sportId: number;
	sportName: string;
}

export interface BestsellerEventsResponseDto {
	sports: BestsellerEventsResponseSportDto[];
}

export interface BestsellerEsportEventsResponseEventGameDto {
	argument?: number | null;
	combinationType: number | null;
	combinationTypes: number[] | null;
	gameCode: number | null;
	gameId: number | null;
	gameName: string | null;
	gameType: number | null;
	marketTypes: number[] | null;
	outcomes: GameOutcomeDto[] | null;
}

export interface BestsellerEsportEventsResponseEventDto {
	categoryId: number;
	category3AggregatedId?: number | null;
	categoryName: string;
	eventCodeId: number;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	games?: BestsellerEsportEventsResponseEventGameDto[] | null;
	gamesCount: number;
	marketTypes: MarketTypeDto[];
	parentId: number;
	parentName: string;
	participants: GameEventParticipantDto[];
	remoteId: number;
	sportId: number;
	sportName: string;
	treatAsSport: number;
	eventExtendedData?: Record<string, any>;
	isLive: boolean | null;
	result?: string | null;
}

export interface BestellerEsportEventsResponseSportDto {
	events: BestsellerEsportEventsResponseEventDto[];
	sportId: number;
	sportName: string;
}

export interface BestsellerEsportEventsResponseDto {
	esports: BestellerEsportEventsResponseSportDto[];
}

export interface EventsByGameIdsResponseEventDto {
	categoryId: number;
	categoryName: string;
	combinationType: number;
	eventCodeId: number;
	eventId: number;
	eventLayout: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	gameCode: number;
	gameId: number;
	gameLayout: number;
	gameName: string;
	gameType: number;
	gamesCount: number;
	isLive: boolean;
	marketTypes: string[];
	outcomes: GameOutcomeDto[];
	parentId: number;
	parentName: string;
	participants: GameEventParticipantDto[];
	remoteId: number;
	sportId: number;
	sportName: string;
	treatAsSport: number;
	eventExtendedData?: Record<string, any>;
	result?: string | null;
	category3AggregatedId?: number | null;
}

export interface EventsByGameIdsResponseDto {
	events: EventsByGameIdsResponseEventDto[];
}

export interface EventQuickSearchResponseResultExtrasDto {
	CATEGORY_ID_1: number;
	CATEGORY_ID_2: number;
	CATEGORY_ID_3: number;
	CATEGORY_NAME_1: string;
	CATEGORY_NAME_2: string;
	CATEGORY_NAME_3: string;
	SPORT_ID: number;
}

export interface EventQuickSearchResponseResultDto {
	area: 'LIVE_EVENT' | 'PREMATCH_EVENT' | 'CATEGORY';
	eventStart: number;
	extras: EventQuickSearchResponseResultExtrasDto;
	id: number;
	name: string;
	score: number;
}

export interface EventQuickSearchResponseDto {
	searchResult: EventQuickSearchResponseResultDto[];
}

export interface EventQuickSearchBodyDto {
	/** langauge code */
	languageCode: string;
	/** word to use in search */
	pattern: string;
}

export interface EventsStatscoreResponseDto {
	/** @example {"111":222,"333":444} */
	eventsStatscore: Record<string, number>;
}

export interface TodayEventsResponseGameDto {
	gameId: number;
	gameName: string;
	gameType: number;
	gameCode: number;
	argument: number;
	combinationType: number;
	marketTypes: number[];
	periodId: number;
	gameLayout: number;
	eventLayout: number;
	combinationTypes?: number[] | null;
	outcomes: GameOutcomeDto[];
}

export interface TodayEventsResponseEventDto {
	categoryId: number;
	categoryName: string;
	category3AggregatedId?: number | null;
	games?: TodayEventsResponseGameDto[] | null;
	eventCodeId?: number | null;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	gamesCount: number;
	isLive: boolean;
	marketTypes: string[];
	parentId: number;
	parentName: string;
	participants: GameEventParticipantDto[];
	remoteId: number;
	sportId: number;
	sportName: string;
	treatAsSport: number;
}

export interface TodayEventsResponseSportDto {
	events: TodayEventsResponseEventDto[];
	sportId: number;
	sportName: string;
}

export interface TodayEventsResponseDto {
	sports: TodayEventsResponseSportDto[];
	gameTypeCodes: Record<string, number[]>;
}

export interface EventByIdResponseGameDto {
	gameId: number;
	gameName: string;
	gameType: number;
	gameCode: number;
	argument: number;
	combinationType: number;
	marketTypes: number[];
	gameLayout: number;
	eventLayout: number;
	outcomes: GameOutcomeDto[];
	combinationTypes?: number[] | null;
	gameTypeDefaultName?: string | null;
	gameTypePattern: string;
	isCashoutAvailable: boolean;
}

export interface EventByIdResponseEventExtendedDataDto {
	neutralGround?: string | null;
	remoteCategoryId?: string | null;
	liveEventId?: number | null;
}

export interface EventByIdResponseEventDto {
	backgroundURL: string;
	eventId: number;
	remoteId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	eventCodeId?: number | null;
	gamesCount: number;
	treatAsSport: number;
	participants: GameEventParticipantDto[];
	sportId: number;
	parentId: number;
	categoryId: number;
	sportName: string;
	parentName: string;
	categoryName: string;
	games?: EventByIdResponseGameDto[] | null;
	marketTypes: MarketTypeDto[];
	isLive: boolean;
	eventExtendedData?: EventByIdResponseEventExtendedDataDto | null;
	category3AggregatedId?: number | null;
}

export interface EventByIdResponseDto {
	event: EventByIdResponseEventDto;
	customeBets: number[];
}

export interface CategoriesResponseChildCategoryDto {
	categoryId: number;
	categoryName: string;
	level: number;
	parentCategory: number;
	eventsCount: number;
	sortOrder: number;
	sportName: string;
	sportId: number;
	treatAsSport: number;
	parentName: string;
	remoteId: number;
	categoryFlag: string;
	disableNoFollow: boolean;
	childrenCounts: number;
}

export interface CategoriesResponseCategoryDto {
	categoryId: number;
	categoryName: string;
	level: number;
	parentCategory: number;
	eventsCount: number;
	sortOrder: number;
	sportName: string;
	sportId: number;
	treatAsSport: number;
	parentName: string;
	disableNoFollow: boolean;
	children: CategoriesResponseChildCategoryDto[];
}

export interface CategoryDto {
	categoryId: number;
	categoryName: string;
	level: number;
	parentCategory: number;
	eventsCount: number;
	sortOrder: number;
	sportName: string;
	sportId: number;
	treatAsSport: number;
	parentName: string;
	remoteId: number;
	categoryFlag: string;
	disableNoFollow: boolean;
}

export interface CategoriesResponseDto {
	categories: CategoriesResponseCategoryDto[];
	popular: CategoryDto[];
}

export interface AllCategoriesResponseDto {
	categories: CategoryDto[];
}

export interface CategoriesDescriptionsResponseDto {
	/** @example {"1":"Description for category with id 1","2":"Description for category with id 2"} */
	descriptions: Record<string, string>;
}

export interface CategoriesFlagsResponse {
	flags: Record<string, string>;
}

export interface EventsByCategoriesResponseEventGameDto {
	gameId: number;
	gameName: string;
	gameType: number;
	gameCode: number;
	argument: number;
	combinationType: number;
	combinationTypes?: number[] | null;
	marketTypes: number[];
	periodId: number;
	gameLayout: number;
	eventLayout: number;
	outcomes: GameOutcomeDto[];
}

export interface EventsByCategoriesResponseEventExtendedDataDto {
	neutralGround?: string | null;
	remoteCategoryId?: string | null;
}

export interface EventsByCategoriesResponseEventDto {
	eventId: number;
	remoteId?: number | null;
	category3AggregatedId?: number | null;
	eventName: string;
	eventStart: number;
	eventType: number;
	eventCodeId?: number | null;
	gamesCount: number;
	treatAsSport: number;
	eventExtendedData: EventsByCategoriesResponseEventExtendedDataDto;
	participants: GameEventParticipantDto[];
	videoStream: boolean;
	sportId: number;
	parentId: number;
	categoryId: number;
	sportName: string;
	parentName: string;
	categoryName: string;
	games: EventsByCategoriesResponseEventGameDto[];
	marketTypes: MarketTypeDto[];
	isLive: boolean;
}

export interface EventsByCategoriesResponseCategoryDto {
	categoryId: number;
	events: EventsByCategoriesResponseEventDto[];
	gameTypes: Record<string, EventsByCategoriesResponseEventGameDto[]>;
}

export interface EventsByCategoriesResponseDto {
	categories: EventsByCategoriesResponseCategoryDto[];
	gameTypes: Record<string, number[]>;
}

export interface EventsByCategoryForDropdownResponseEventDto {
	eventId: number;
	eventName: string;
	eventStart: number;
}

export interface EventsByCategoryForDropdownResponseDto {
	events: EventsByCategoryForDropdownResponseEventDto[];
}

export interface SessionInfoOfferPlayerInfoDto {
	imageUrl?: string | null;
	playerInfo?: string | null;
	rulesPageUrl?: string | null;
}

export interface SessionInfoBonusBalanceDto {
	amount: number;
	currency: string;
	customerId?: number | null;
	name: string;
	nameKey: string;
	offerDefinitionId: number;
	offerPlayerInfo: SessionInfoOfferPlayerInfoDto;
	suggestedMaxStake?: number | null;
	suggestedMinStake?: number | null;
	wagerBonusId: number;
}

export interface SessionInfoDto {
	customerId: number;
	accountStatus: number;
	language: string;
	login: string;
	firstName: string;
	lastName: string;
	email: string;
	balance: number;
	bonusBalance?: number | null;
	currencyCode: string;
	vip: boolean;
	bonusCode: string;
	forData: object;
	mobileNumber: string;
	depositCounter: number;
	depositInfo: string;
	sessionStart: number;
	registrationDate: number;
	bonusBalanceList: SessionInfoBonusBalanceDto[];
}

export interface LoginResponseDto {
	loginData: SessionInfoDto;
	lastStake: number;
}

export interface LoginDto {
	/** @example "login" */
	login: string;
	/** @example "password" */
	password: string;
	/** Recaptcha Token to assess if login done by bot. */
	recaptchaToken?: string;
}

export interface LogoutResponseDto {
	isLogout: boolean;
}

export interface KeepAliveResponseDto {
	customerData: SessionInfoDto;
	/** Present only if needLastStake parameter was set to true */
	lastStake?: number;
}

export interface PastSessionDto {
	loginDate: number;
	logoutDate: number;
	sesionUUID: string;
	/**
	 *
	 * * 0 - Terminated
	 * * 1 - Active
	 */
	sessionStatus: 0 | 1;
}

export interface SessionHistoryResponseDto {
	sessions: PastSessionDto[];
	totalSessions: number;
}

export interface AccountLimitsResponseDataDto {
	limitType: number;
	defaultLimitValue?: number | null;
	defaultMaxLimitValue?: number | null;
	limitAmount?: number | null;
	oldLimitAmount?: number | null;
	requestedLimitAmount?: number | null;
	requestedLimitDate?: number | null;
	requestedLimitValidFrom?: number | null;
}

export interface AccountLimitsResponseDto {
	limits: AccountLimitsResponseDataDto[];
}

export interface UpdateLimitDataDto {
	/** limit type id */
	limitType: number;
	/** limit amount/value */
	limitAmount: number;
}

export interface UpdateLimitBodyDto {
	/** array of limits which needs to update */
	limits: UpdateLimitDataDto[];
}

export interface AccountRegistrationDetailsDto {
	adminId?: number | null;
	assignedToCashier?: number | null;
	countryOfBirth?: string | null;
	customerId?: number | null;
	enumData?: string | null;
	nationality: string | null;
	personalIdentifier?: string | null;
	referenceId: number;
	referenceInput?: string | null;
	referralCode: number | null;
	referrer: number | null;
	regDate: number;
	regIp: string;
	verificationCode: string;
	verificationDate: number;
	verificationIp: string;
	verificationMode: string;
}

export interface AccountDataDto {
	accountStatus: string;
	address: string;
	area: string;
	birthDate: number;
	city: string;
	countryCode: string;
	countryOfBirth: string;
	currencyCode: string;
	customerId?: number | null;
	dateFormat: string;
	email: string;
	firstName: string;
	languageCode: string;
	lastName: string;
	loginName: string;
	mobileNumber: string;
	namePrefix: string;
	notes: string | null;
	oddsFormat: string;
	personalIdentifier: string | null;
	postalCode: string;
	registeredOn: number;
	registrationDetails: AccountRegistrationDetailsDto;
	stateId: number;
	timeFormat: string;
	timeZoneCode: string;
	validUntil: number;
	verificationStatus: string;
}

export interface VerificationStatusResponseCustomerDataDto {
	accountStatus: number;
	address: string;
	city: string;
	country: string;
	customerId: number;
	dateOfBirth: number;
	email: string;
	firstName: string;
	gender: number;
	languageCode: string;
	lastName: string;
	loginName: string;
	mobile: string;
	nationality: string;
	pepAccepted: boolean;
	personalIdentifier?: string | null;
	postalCode: string;
	receiveEmails: boolean;
	receiveMails: boolean;
	receiveSms: boolean;
	receiveTradeInformation: boolean;
	stateId: number;
	bonusCode?: string | null;
}

export interface IdentificationDocumentDto {
	expiryDate: number | null;
	id: number;
	number: string;
	status: string;
	type: string;
	typeId: number;
}

export interface VerificationStatusResponseDataDto {
	account: AccountDataDto;
	customerData: VerificationStatusResponseCustomerDataDto;
	identificationDocuments: IdentificationDocumentDto[];
}

export interface VerificationStatusResponseDto {
	verificationStatusData: VerificationStatusResponseDataDto;
}

export interface ChangePasswordResponseDto {
	isPasswordChanged: boolean;
}

export interface ChangePasswordDto {
	/** new password */
	newPassword: string;
	/** old password */
	oldPassword: string;
}

export interface PreRegisterResponseDto {
	isCodeSent: boolean;
}

export interface PreRegisterBodyDto {
	/** email of user */
	email: string;
	/** email consent */
	emailConsent: boolean;
	/** mobile no of user */
	mobile: string;
	/** sms consent */
	smsConsent: boolean;
}

export interface RegisterResponseDto {
	registrationStatus: number;
}

export interface RegisterBodyDto {
	/** first name of user */
	firstName: string;
	/** last name of user */
	lastName: string;
	/** login name of user, same as email id */
	loginName: string;
	/** password of user */
	password: string;
	/** email of user */
	email: string;
	/** mobile no of user */
	mobile: string;
	/** whether user selected welcome bonus or not */
	bonus: boolean;
	/** verification code sent to user mobile */
	verificationCode: string;
	/** whether user want to receive email or not */
	receiveEmail: boolean;
	/** whether user want to receive SMS or not */
	receiveSms: boolean;
	/**
	 * user platform
	 * @example ["desktop","mobile"]
	 */
	channel: string;
	/**
	 * user browser
	 * @example ["microsoft","safari"]
	 */
	browser: string;
	/**
	 * user browser
	 * @example ["desktop","ipad"]
	 */
	device: string;
	/** applied promotion/registration codes */
	registrationOffers?: string[];
	/** fetch it from google analytics cookie -> __utmzz */
	clickId?: string;
	/** nationality of user */
	nationality?: string;
	/** PESEL number of user */
	personalIdentificationNumber?: string;
	/**
	 * Date of birth of user
	 * @format yyyy-mm-dd
	 */
	dateOfBirth?: string;
	/** chosen promotionId */
	promotion?: object;
}

export interface ActivateAccountBodyDto {
	/** customer id from email */
	customerId: number;
	/** activation code from email */
	activationCode: string;
}

export interface ResetPasswordDto {
	/** registered email address */
	email: string;
}

export interface ResetPasswordVerifyBodyDto {
	/** new password */
	password: string;
	/** customer id */
	customerId: number;
	/** verification code from email */
	verificationCode: string;
}

export interface CloseAccountResponseDto {
	isClosed: boolean;
}

export interface UpdateSelfExclusionBodyDto {
	/**
	 * for how many days for exclusion
	 * @min 1
	 * @default 1
	 */
	days: number;
}

export interface CollapsedMarketsDto {
	prematch?: object;
	live?: object;
	popular?: object;
}

export interface AvatarColorScheme {
	/** Background color of the avatar. */
	backgroundColor: string;
	/** Font color used in the avatar. */
	fontColor: string;
}

export interface AvatarImage {
	/** Unique identifier for the avatar image. */
	avatarId: number;
	/** URL to the avatar image. */
	avatarUrl: string;
}

export interface Promotion {
	promotion_id: string;
	promotion_name: string;
}

export interface PlayersProfileData {
	collapsedMarkets?: CollapsedMarketsDto;
	/** Player's VIP level indicating their status in the system. */
	vipLevel: number;
	/** The ID of the avatar assigned to the player. */
	avatarId: number;
	/** URL or path to the avatar file. */
	avatarFile: string;
	/** Indicates whether the avatar is a default avatar. */
	avatarIsDefault: boolean;
	/** Color scheme for the avatar's background and font. */
	avatarColorScheme: AvatarColorScheme;
	/** List of collected avatars by the player. */
	collectedAvatars: AvatarImage[];
	/** Player's initials used in their profile. */
	playerInitials: string;
	/** Player's first name. */
	playerFirstName: string;
	/** Player's last name. */
	playerLastName: string;
	/** Default stake list for the player. */
	defaultStakes: string[];
	/** Default payment method used by the player. */
	defaultPaymentMethod: string;
	/** Last payment method used by the player. */
	lastDepositMethod: string;
	/** ID of the bank associated with the last deposit. */
	lastDepositBankId: number;
	/** List of promotions the player is currently enrolled in. */
	promotions: Promotion[];
	/** List of promotions that the player is no longer enrolled in. */
	inactivePromotions: Promotion[];
	/** The total number of bets placed by the player. */
	totalBets: number;
	/** The largest odd value the player has bet on. */
	biggestOdd: number;
	/** The biggest win the player has had. */
	biggestWin: number;
	/** Player's favourite category for betting based on SBB data. */
	favouriteCategory: number;
	/** Player's chosen promotion while registering */
	promotion: object;
	favouriteCategoryName: string;
	emptyStatistics: boolean;
	favouriteSportIcon: string;
}

export interface AccountInformationResponseBankDetailsDto {
	accountId: number;
	accountNumber: string;
	accountOwnerName?: string | null;
	accountType: number;
	bankId: number;
	bankName: string;
	payoutMessage: string;
}

export interface AccountInformationResponseVerificationStatusDto {
	account: AccountDataDto;
	customerData: SessionInfoDto;
	identificationDocuments: IdentificationDocumentDto[];
}

export interface AccountInformationResponseDto {
	playersProfile: PlayersProfileData;
	bankDetails: AccountInformationResponseBankDetailsDto;
	verificationStatusData: AccountInformationResponseVerificationStatusDto;
}

export interface FinishVerificationResponseDto {
	isSuccess: boolean;
}

export interface CustomerDataSchemaDto {
	/** address of user */
	address: string;
	/** city of user */
	city: string;
	/** postal code of user */
	postalCode: string;
	/** true or false about user selection on PEP */
	pepAccepted: true | false;
	/** source of income selected by user */
	giifType: object;
	/** bank account no of user */
	bankAccountNumber: string;
}

export interface DocumentDataSchemaDto {
	/** document type id based on user selection */
	documentType: string;
	/** document expiration date */
	expirationDate: string;
	/** document number, it must be unique and need to validate using is-value-unique API */
	serialNumber: string;
	/** it must be set to disk */
	uploadType: 'DISK';
}

export interface FinishVerificationBodyDto {
	/** document type id based on user selection */
	customerData: CustomerDataSchemaDto;
	/** document type id based on user selection */
	documentData: DocumentDataSchemaDto;
	file1: File;
	file2?: File;
}

export interface ApprovedSlipsResponseDataDto {
	customerId: number;
	newPossibleWin: string;
	newStake: number;
	oddsChanges: string;
	possibleWin: number;
	regMethod: number;
	slipId: number;
	slipJSON: string;
	slipType: number;
	stake: number;
	status: number;
	traderAcceptationTime: number;
	customSlipId: number;
}

export interface ApprovedSlipsResponseDto {
	approvedSlipData: ApprovedSlipsResponseDataDto;
}

export interface ConsentTagResponseDataDto {
	definitionContainerName: string;
	definitionId: number;
	definitionName: string;
	definitionType: string;
	isAccepted?: boolean | null;
	tags: string[];
	withCustomData: boolean;
}

export interface ConsentTagResponseDto {
	consentTagsData: ConsentTagResponseDataDto[];
}

export interface ChangeConsentResponseDto {
	changeConsentData: boolean;
}

export interface BodyDto {
	definitionId: string;
	accepted: boolean;
}

export interface ChangeConsentRequestDto {
	preference: BodyDto[];
}

export interface AuxiliaryData {
	mobileCorrect: boolean;
	pepAccepted: boolean;
	emailCorrect: boolean;
}

export interface IdentificationDocument {
	number: string;
	typeId: number;
	type: string;
	expiryDate: number | null;
}

export interface PersonalDataResponseDto {
	customerId: number;
	gender: 1 | 2;
	firstName: string;
	lastName: string;
	address: string;
	city: string;
	countryCode: string;
	languageCode: string;
	state: string;
	loginName: string;
	email: string;
	dateOfBirth: number;
	dateOfBirthAsString: string;
	accountBalance: number;
	currencyCode: string;
	mobileNumber: string;
	verificationStatus: boolean;
	accountStatus: number;
	nationality: string;
	personalIdentifier: string;
	postalCode: string;
	auxiliaryData: AuxiliaryData;
	bonusCode: string | null;
	identificationDocument?: IdentificationDocument;
	registrationDate: number;
	verificationTypeEnabled2FA: number;
}

export interface TotalBonusDto {
	currentBonusValue: number;
	futureBonusValue: number;
	isTotalbonusAvailable: boolean;
	minOutcomeOdds: number;
}

export interface PossibleWinningResponseDto {
	bonus: number;
	tax: number;
	totalOdds: number;
	totalStake: number;
	winning: number;
	totalBonus: TotalBonusDto;
	totalWinning: number;
}

export interface RefreshedOddsDto {
	active: boolean;
	banker: boolean;
	eventType: number;
	isCompleted: boolean;
	isFinished: boolean;
	outcomeId: number;
	outcomeOdds: number;
	outcomeUpdateState: number;
	showWarning: boolean;
	sportId: number;
	eventId?: number | null;
	eventStart?: number | null;
	selectedBet?: boolean | null;
}

export interface CalculateTaxAndWinningResponseDto {
	/** Present only if needWinningCalculation is set to true */
	taxFactor?: number;
	/** Present only if needWinningCalculation is set to true */
	possibleWinning?: PossibleWinningResponseDto;
	refreshedOdds: RefreshedOddsDto[];
	/** Present only if needWinningCalculation is set to true */
	newPossibleWinning?: PossibleWinningResponseDto;
}

export interface OutcomeDto {
	/** outcome id */
	outcomeId: number;
	/** current outcome odds */
	outcomeOdds: number;
	/** is this banker odds */
	banker: boolean;
	/** sports id */
	sportId: number;
	/** event id */
	eventId: number;
	/** game id */
	gameId: number;
	/** event type */
	eventType: number;
	/** event start time */
	eventStart: number;
	/** whether this outcome selected or not */
	selectedBet?: boolean;
}

export interface CalculateTaxWinningDto {
	/**
	 * total placed stake
	 * @min 0
	 */
	stake: number;
	/** bet type */
	betType: number;
	/** whether given outcomes will accumulate or not */
	addAccumulator: boolean;
	/** accept higher odds if changed during calculation */
	acceptHigherOdds: boolean;
	/** accept any odds if changed during calculation */
	acceptAnyOdds: boolean;
	/** outcome array which used for calculation */
	outcomes: OutcomeDto[];
	/** currency code */
	currencyCode: string;
	/** is this bonus bet */
	bonusBet: boolean;
	/** if bonusBet true then provide wager bonus id */
	wagerBonusId?: string;
	/** outcome array of array which used for calculation */
	blocks: OutcomeDto[][];
	/** country code */
	countryCode?: string;
	/** is count downed */
	countdowned?: boolean;
	/** hash of bet slip */
	betSlipHash?: string;
	/** quick slip id */
	quickSlipId?: string;
	isFreeBetSelected: boolean;
	freeBetStake: number;
	/** id of freebet if user want to use freebet provided to him */
	freebetId?: number;
	/** whether tax/winning calculation needed or not */
	needWinningCalculation?: boolean;
}

export interface NotFoundErrorDto {
	/** @example true */
	isError: boolean;
	message: string;
	/** @example 404 */
	code: number;
}

export interface BetActionErrorUpdatedOutcomeDto {
	outcomeId: number;
	outcomeName: string;
	outcomeOdds: number;
	outcomePosition: number;
	gameId: number;
	active: boolean;
}

export interface BetActionErrorResponseDataDto {
	betSlipErrorCode: number;
	referenceObject: number | string;
	referenceObjectName: string;
	suggestedErrorMessage?: string | null;
	suggestedErrorMessageKeys?: string[] | null;
	/** Object containing additional information regarding the error */
	errorDetails?: Record<string, string>;
	updatedOutcome?: BetActionErrorUpdatedOutcomeDto | null;
}

export interface BetActionErrorResponseDto {
	isError: boolean;
	code: number;
	message: string;
	data: BetActionErrorResponseDataDto[];
}

export interface SlipDataResponseDto {
	bonus: number;
	newBalance: number;
	slipId: number;
}

export interface PlaceBetResponseDto {
	slipData: SlipDataResponseDto;
}

export interface ApproveBetResponseDto {
	slipData: SlipDataResponseDto;
}

export interface BestOutcomeDto {
	banker: boolean;
	combinationType: number;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventStatus: number;
	eventType: number;
	gameId: number;
	gameName: string;
	live: boolean;
	odds: number;
	outcomeId: number;
	outcomeName: string;
	sportId: number;
}

export interface FindBestOutcomesResponseDto {
	outcomes: BestOutcomeDto[];
}

export interface ValidationResultDto {
	message: string;
	odds: number;
	result: boolean;
}

export interface ValidateCustomBetsResponseDto {
	validationResult: ValidationResultDto;
}

export interface CustomBetsCalculationOutcomeDto {
	eventId: number;
	gameId: number;
	gameName: string;
	name: string;
	odds: number;
	outcomeId: number;
	result: number;
	status: number;
}

export interface CalculateCustomBetsResponseDto {
	outcome: CustomBetsCalculationOutcomeDto;
}

export interface UpdateCustomBetOddsOutcomeResponseDto {
	eventId: number;
	gameId: number;
	gameName: string;
	name: string;
	odds: number;
	outcomeId: number;
	result: number;
	status: number;
}

export interface UpdateCustomBetOddsResponseDto {
	outcome: UpdateCustomBetOddsOutcomeResponseDto;
}

export interface TransactionByDateResponseTransactionDetailsDto {
	eventStart: number;
	eventName: string;
	gameName: string;
	outcomeName: string;
	outcomeResult: string;
	score: string;
	odds: number;
	statusCode: number;
	banker?: boolean | null;
	categoryName1: string;
	categoryName2: string;
	categoryName3: string;
	cat3DisplayedAs: string;
	sportId: number;
	eventId: number;
	remoteId?: number | null;
	gameId: number;
	outcomeId: number;
	eventType: number;
	categoryLev2: number;
	categoryLev3: number;
	isEsport: boolean;
	argument?: number | null;
	gameTypeId: number;
	isWon: boolean;
	liveEventId?: number | null;
	brLiveEventIdRunningForPrematch?: number | null;
	blockId?: number | null;
}

export interface SlipCombinationDto {
	bonus?: number | null;
	confirmedReturn: number;
	odds: number;
	oddsNoGain: number;
	outcomeIds: number[];
	possibleReturn: number;
	stake: number;
	status: number;
	tax: number;
}

export interface SlipCombinationsDto {
	combinations: SlipCombinationDto[];
}

export interface TransactionByDateResponseTransactionDto {
	bonus?: number | null;
	confirmedReturn?: number | null;
	decidedDate?: number | null;
	isFreebet?: boolean | null;
	possibleReturn?: number | null;
	regDate: number;
	regMethod?: number | null;
	resultText?: string | null;
	showCombinationDetails: boolean | null;
	transactionId: number;
	slipType?: number | null;
	stake?: number | null;
	statusCode?: number | null;
	tax?: number | null;
	taxConfirmed?: number | null;
	taxFactor: number;
	totalOdds: string;
	totalOddsWithoutSalesTaxFactor?: number | null;
	transactionsCount: number;
	transactionType: number;
	transactionDetails: TransactionByDateResponseTransactionDetailsDto[];
	offerDefinitionId?: number | null;
	slipCombinations?: SlipCombinationsDto | null;
}

export interface TransactionByDateResponseDto {
	offset: number;
	pageToken: string;
	transactions: TransactionByDateResponseTransactionDto[];
}

export type AnnotateTransactionDto = object;

export interface TransfersByDateResponseTransferDto {
	amount: number;
	currencyCode: string;
	customerId: number;
	referenceId: number | null;
	regDate: number;
	responseText: string | null;
	statusCode: string;
	transactionId: number;
	transactionSwitch: number;
	type: number;
}

export interface TransfersByDateResponseDto {
	transfers: TransfersByDateResponseTransferDto[];
}

export interface CustomerHasPlacedBetResponseDto {
	customerHasPlacedBet: boolean;
}

export interface SlipsSettledAfterLastLoginResponseTransactionDetailsDto {
	eventStart: number;
	eventName: string;
	gameName: string;
	outcomeName: string;
	outcomeResult: string;
	score: string;
	odds: number;
	statusCode: number;
	banker?: boolean | null;
	categoryName1: string;
	categoryName2: string;
	categoryName3: string;
	cat3DisplayedAs: string;
	sportId: number;
	eventId: number;
	remoteId?: number | null;
	gameId: number;
	outcomeId: number;
	eventType: number;
	categoryLev2: number;
	categoryLev3: number;
	isEsport: boolean;
	argument?: number | null;
	gameTypeId: number;
	liveEventId?: number | null;
	brLiveEventIdRunningForPrematch?: number | null;
	blockId?: number | null;
	virtualType?: string | null;
	gameInfo?: Record<string, string>;
	extras?: Record<string, any>;
	participants?: string | null;
	modifiedOn: number | null;
	changeType: string | null;
	earlyPayoutAvailable: boolean | null;
	combinationType: number | null;
	combinationTypes?: number[] | null;
}

export interface SlipsSettledAfterLastLoginResponseTransactionDto {
	bonus?: number | null;
	confirmedReturn?: number | null;
	decidedDate?: number | null;
	isFreebet?: boolean | null;
	possibleReturn?: number | null;
	regDate: number;
	regMethod?: number | null;
	resultText?: string | null;
	showCombinationDetails?: boolean | null;
	transactionId: number;
	slipType?: number | null;
	stake?: number | null;
	statusCode?: number | null;
	tax?: number | null;
	taxConfirmed?: number | null;
	totalOdds: number;
	totalOddsWithoutSalesTaxFactor?: number | null;
	transactionsCount: number;
	transactionType: number;
	transactionDetails: SlipsSettledAfterLastLoginResponseTransactionDetailsDto[];
	offerDefinitionId?: number | null;
	slipCombinations?: SlipCombinationsDto | null;
}

export interface SlipsSettledAfterLastLoginResponseDto {
	settledSlipData: SlipsSettledAfterLastLoginResponseTransactionDto[];
}

export interface AvailableCashoutDataDto {
	amount: number;
	countDownRequired: boolean;
}

export interface AvailableCashoutResponseDto {
	cashoutData: AvailableCashoutDataDto;
}

export interface FullCashoutDataDto {
	currentCashoutAmount: number;
	delayedCashoutAvailable: boolean;
	errorMessage: string;
	messageKey: string;
	success: boolean;
}

export interface FullCashoutResponseDto {
	fullCashoutData: FullCashoutDataDto;
}

export interface FullCashoutBodyDto {
	/** amount change will be accepted for cashout or not */
	customerAcceptedAmountChange: boolean;
	/** cashout amount requested/visible to user */
	requestedAmount: number;
}

export interface PrepareLiveCountdownDataDto {
	/** Required time in miliseconds */
	value: string;
}

export interface PrepareLiveCountdownResponseDto {
	prepareCashoutData: PrepareLiveCountdownDataDto;
}

export interface PrepareCashoutBodyDto {
	/** amount change will be accepted for cashout or not */
	customerAcceptedAmountChange: boolean;
	/** cashout amount requested/visible to user */
	requestedAmount: number;
}

export interface BannersResponseEventGameOutcomeDto {
	outcomeId: number;
	outcomeName: string;
	outcomeOdds: number;
	status: number;
}

export interface BannersResponseEventGameDto {
	categoryName: string;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	gameId: number;
	gameName: string;
	gameType: number;
	outcomes: BannersResponseEventGameOutcomeDto[];
	parentName: string;
	sportId: number;
	sportName: string;
}

export interface BannersReponseDataDto {
	bannerId: string;
	/** @format date-time */
	dateFrom?: string | null;
	/** @format date-time */
	dateTill?: string | null;
	eventGame: BannersResponseEventGameDto;
	eventGameId: string;
	sortOrder: number;
	imageAltText?: string | null;
	imageUrl?: string | null;
	link?: string | null;
	subTitle?: string | null;
	title?: string | null;
	/** @default false */
	isLive?: boolean;
}

export interface BannersResponseDto {
	banners: BannersReponseDataDto[];
}

export interface SubBannersResponseDataDto {
	/** @format date-time */
	dateFrom?: string | null;
	/** @format date-time */
	dateTill?: string | null;
	sortOrder: number;
	isTimed: boolean;
	subBannerId: string;
	imageAltText?: string | null;
	imageUrl?: string | null;
	link?: string | null;
	title?: string | null;
}

export interface SubBannersResponseDto {
	subBanners: SubBannersResponseDataDto[];
}

export interface RegistrationBannersResponseDataDto {
	/** @format date-time */
	type?: string;
	url?: string;
}

export interface RegistrationBannersResponseDto {
	banners: RegistrationBannersResponseDataDto[];
}

export interface EntertainmentMarketType {
	marketType: number;
	gameIds: number[];
}

export interface EntertainmentResponseEventGameOutcomeDto {
	outcomeId: number;
	outcomeName: string;
	outcomeOdds: number;
	status: number;
}

export interface EntertainmentsResponseEventGameDto {
	categoryId: number;
	categoryName: string;
	combinationType: number;
	eventCodeId: number;
	eventId: number;
	eventLayout: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	gameCode: number;
	gameId: number;
	gameLayout: number;
	gameName: string;
	gameType: number;
	gamesCount: number;
	marketTypes: EntertainmentMarketType[];
	outcomes: EntertainmentResponseEventGameOutcomeDto[];
	parentId: number;
	parentName: string;
	participants: string[];
	remoteId: number;
	sportId: number;
	sportName: string;
	treatAsSport: number;
	eventExtendedData?: object;
	isLive?: boolean;
	result?: string;
}

export interface EntertainmentsResponseDataDto {
	category?: string | null;
	dateFrom?: string | null;
	dateTill?: string | null;
	entertainmentId: string;
	eventGame: EntertainmentsResponseEventGameDto;
	eventGameId: string;
	imageAltText?: string | null;
	imageUrl?: string | null;
	subTitle?: string | null;
	title?: string | null;
}

export interface EntertainmentsResponseDto {
	entertainments: EntertainmentsResponseDataDto[];
	total: number;
}

export interface PlacementsResponseDto {
	placements: PlacementsResponseDto[];
}

export interface RegulationTranslationResponseDto {
	fileUrl?: string;
	title?: string;
	regulation_description?: string;
}

export interface PromotionResponsePrimaryButtonDto {
	link: string;
	text: string;
}

export interface PromotionsResponseDataDto {
	/** @format date-time */
	dateFrom?: string | null;
	/** @format date-time */
	dateTill?: string | null;
	isTimed: boolean;
	promotionId: string;
	promotionName?: string | null;
	promotionNameSlug: string;
	imageAltText?: string;
	imageUrl?: string;
	primaryButton?: PromotionResponsePrimaryButtonDto;
	offerDefinitionId?: number;
	summary?: string;
	title?: string;
}

export interface PromotionsResponseDto {
	promotions: PromotionsResponseDataDto[];
	total: number;
}

export interface PromotionResponseDataDto {
	description: string;
	promotionId: string;
	offerDefinitionId?: number;
	imageAltText?: string | null;
	imageUrl?: string | null;
	title?: string | null;
	/** @format date-time */
	dateTill?: string | null;
	isTimed: boolean;
}

export interface PromotionResponseDto {
	promotion: PromotionResponseDataDto;
	redirectUrl: string;
	redirect: boolean;
}

export interface PromotionParameterDto {
	name: string;
	value: string;
	icon: string;
	type: string;
	realValue?: string | string[] | number | number[];
}

export interface RegistrationPromotionResponseDto {
	promotionId: string;
	promotionName: string;
	/** @format date-time */
	registrationDateTill: string | null;
	summary?: string;
	parameters: PromotionParameterDto[];
	existCode?: boolean;
}

export interface RegistrationPromotionsResponseDto {
	promotions: RegistrationPromotionResponseDto[];
	total: number;
}

export interface AppTranslationsResponseDto {
	language: string;
	languageData?: Record<string, string>;
}

export interface MybetRequestDto {
	email?: string;
	description: string;
	/** @format date-time */
	dateEvent?: string;
}

export interface CustomPageResponseTranslationDescriptionDto {
	value?: string;
	isReadyHtml?: boolean;
}

export interface CustomPageResponseTranslationDataDto {
	description: CustomPageResponseTranslationDescriptionDto;
	imageAltText?: string | null;
	imageUrl?: string | null;
	subTitle?: string | null;
	title?: string | null;
}

export interface CustomPageResponseDataDto {
	customPageId: string;
	customPageName?: string | null;
	customPageNameSlug: string;
	isActive?: boolean | null;
	title?: string | null;
	description?: string | null;
	ogImage?: string | null;
	pageData: CustomPageResponseTranslationDataDto;
	link: string;
}

export interface CommonMessageResponseDto {
	/** @example false */
	isError: boolean;
	message: string;
	code: number;
}

export interface PromoCodeRequestDto {
	userId: string;
	promoCode: string;
}

export interface SportEventImageResponseDataDto {
	eventId?: string | null;
	imageUrl?: string | null;
}

export interface SportEventImageResponseDto {
	sportEventImages: SportEventImageResponseDataDto[];
}

export interface BetShopResponse {
	id: number;
	name: string;
	address: string;
	city: string;
	latitude: number;
	longitude: number;
	mon: string;
	tue: string;
	wed: string;
	thu: string;
	fri: string;
	sat: string;
	sun: string;
}

export interface GetBetShopResponseDto {
	betShops: BetShopResponse[];
}

export interface SeoResponseDto {
	name: string;
	type: object;
	title: string;
	description: string;
	categoryId: number;
	categoryName: string;
	level: number;
	parentCategory: number;
	sportId: number;
	parentName: string;
	sportName: string;
	metaTitle: string;
	metaDescription: string;
}

export interface BetmatchEntryResponseDto {
	/**
	 * Unique identifier of the event.
	 * @example 12345
	 */
	eventId: number;
	/**
	 * Unique identifier of the game.
	 * @example 67890
	 */
	gameId: number;
	/**
	 * Unique identifier of the outcome.
	 * @example 98765
	 */
	outcomeId: number;
	/**
	 * Name of the outcome.
	 * @example "1X2"
	 */
	outcomeName: string;
	/**
	 * Odd value associated with the outcome.
	 * @example 1.85
	 */
	oddValue: number;
	/**
	 * Headline or description of the match.
	 * @example "Exciting match between Team A and Team B."
	 */
	headline: string;
	/**
	 * Category name of the match.
	 * @example "Premier League"
	 */
	categoryName: string;
	/**
	 * Name of the event.
	 * @example "FC Barcelona vs Milan Milanówek"
	 */
	eventName: string;
	/**
	 * Start time of the event in UNIX timestamp.
	 * @example 1672531200
	 */
	eventStart: number;
	/**
	 * Type of the event (e.g., 1 for live, 2 for prematch).
	 * @example 2
	 */
	eventType: number;
	/**
	 * Name of the game.
	 * @example "Football"
	 */
	gameName: string;
}

export interface BetmatchResponseDto {
	/**
	 * Name of the betmatch.
	 * @example "Betmatch Christmas 2024"
	 */
	betmatchName: string;
	/**
	 * Title of the betmatch that appears in the application.
	 * @example "Betmatch Christmas 2024"
	 */
	betmatchTitle: string;
	/**
	 * Description of the betmatch.
	 * @example "Best events picked during christmas."
	 */
	betmatchDescription: string;
	/**
	 * Start date of the betmatch.
	 * @format date-time
	 * @example "2024-01-01T00:00:00.000Z"
	 */
	betmatchFrom: string;
	/**
	 * End date of the betmatch.
	 * @format date-time
	 * @example "2024-01-31T23:59:59.000Z"
	 */
	betmatchTill: string;
	/** Main image URL for the betmatch. */
	betmatchImage: string;
	/**
	 * Final text or message for the betmatch.
	 * @example "Thank you for participating in the Betmatch Christmas 2024!"
	 */
	betmatchFinalText: string;
	/**
	 * Headlines related to the betmatch cards.
	 * @example ["Headline 1","Headline 2"]
	 */
	betmatchHeadlines: string[];
	/** List of matches / events included in the betmatch. */
	matches: BetmatchEntryResponseDto[];
}

export interface OfferPlayerInfo {
	imageUrl?: string | null;
	rulesPageUrl?: string | null;
	playerInfo?: string | null;
	offerCode?: string | null;
}

export interface BonusBalanceValueDto {
	amount: number;
	currency: string;
	zero: boolean;
	centsLong: number;
	positiveAmount: boolean;
	negativeAmount: boolean;
	cents: number;
}

export interface BetSlipConditionsDto {
	slipRegisterMethods?: number[] | null;
	slipBetTypes?: number[] | null;
	outcomeAllInEventTypes?: number[] | null;
	outcomeOneInEventTypes?: number[] | null;
	outcomeOneInGameTypes?: number[] | null;
	outcomeAllInGameTypes?: number[] | null;
	outcomeNoneInGameTypes?: number[] | null;
	slipMinStake?: number | null;
	slipMaxStake?: number | null;
	slipMaxNumberOfOutcomes?: number | null;
	slipMinNumberOfOutcomes?: number | null;
	combinationMinOdds?: number | null;
	combinationMaxOdds?: number | null;
	outcomeMinOdds?: number | null;
	combinationMinNumberOfOutcomes?: number | null;
	outcomeAllInSportIds?: number[] | null;
	outcomeOneInSportIds?: number[] | null;
	outcomeAllInParentCategoryIds?: number[] | null;
	outcomeOneInParentCategoryIds?: number[] | null;
	outcomeAllInCategoryIds?: number[] | null;
	outcomeOneInCategoryIds?: number[] | null;
	outcomeNoneInCategoryIds?: number[] | null;
}

export interface ActiveBonusDto {
	customerId: number;
	descriptionKey: string;
	nameKey: string;
	offerDefinitionId: number;
	offerId: number;
	offerName: string;
	offerPlayerInfo: OfferPlayerInfo;
	offerType: 'WAGER_BONUS' | 'SPORT_BET_FREEBET' | 'TAX_FACTOR_PROMOTION' | 'SPORT_BET_CASHBACK';
	totalToWager: number;
	validTo: number;
	wagerBonusId: number;
	maxBonusAmount: number;
	currentBonusAmount: number;
	leftToWager: number;
	wageredPercent: number;
	offerStatus: 'ACTIVE';
	activationTime: number;
	balanceValue: BonusBalanceValueDto;
	bonusUnlocked: boolean;
	betSlipConditions: BetSlipConditionsDto;
}

export interface HistoricalBonusDto {
	customerId: number;
	descriptionKey: string;
	nameKey: string;
	offerDefinitionId: number;
	offerId: number;
	offerName: string;
	offerPlayerInfo: OfferPlayerInfo;
	offerType: 'WAGER_BONUS' | 'SPORT_BET_FREEBET' | 'TAX_FACTOR_PROMOTION' | 'SPORT_BET_CASHBACK';
	totalToWager: number;
	validTo: number;
	offerStatus: 'OFFER_EXPIRED' | 'HISTORY' | 'OFFER_COMPLETED_AND_CLOSED';
	wagerBonusId: number;
	maxBonusAmount: number;
	currentBonusAmount: number;
	leftToWager: number;
	wageredPercent: number;
	activationTime: number;
	statusUpdatedOn: number;
}

export interface QueuedBonusActivationSource {
	sourceOfferDefinitionId?: number | null;
	sourceOfferId?: number | null;
	sourceAdminId?: number | null;
	sourceBetSlipId?: number | null;
	sourceTransactionId?: number | null;
	sourceAssignCodeId?: number | null;
	sourceLottoCouponId?: number | null;
}

export interface QueuedBonusAdditionalDataDto {
	adminId?: number | null;
	ip?: string | null;
	activationSource: QueuedBonusActivationSource;
	sourceBetSlipId?: number | null;
	info?: string | null;
	value: number;
}

export interface QueuedBonusDto {
	customerId: number;
	descriptionKey: string;
	nameKey: string;
	offerDefinitionId: number;
	offerId: number;
	offerName: string;
	offerPlayerInfo: OfferPlayerInfo;
	offerType: 'WAGER_BONUS' | 'SPORT_BET_FREEBET' | 'TAX_FACTOR_PROMOTION' | 'SPORT_BET_CASHBACK';
	totalToWager: number;
	validTo: number;
	offerQueueId: number;
	activationValue: number;
	sourceId: number;
	eventAdditionalData: QueuedBonusAdditionalDataDto;
	bonusValue: number;
	wagerTimes: number;
	identifier: string;
}

export interface BonusListDto {
	active: ActiveBonusDto[];
	history: HistoricalBonusDto[];
	queue: QueuedBonusDto[];
}

export interface BonusResponseDto {
	bonusList: BonusListDto;
}

export interface CancelActiveBonusResponseDto {
	canceled: boolean;
}

export interface ActiveFreebetDto {
	offerName: string;
	offerId: number;
	nameKey: string;
	descriptionKey: string;
	offerDefinitionId: number;
	customerId: number;
	offerPlayerInfo: OfferPlayerInfo;
	offerFreebetId: number;
	validTo: number;
	freebetStake: number;
	offerType: 'SPORT_BET_FREEBET';
	offerStatus: 'ACTIVE';
	activationTime: number;
	betSlipConditions: BetSlipConditionsDto;
}

export interface HistoricalFreebetDto {
	offerName: string;
	offerId: number;
	nameKey: string;
	descriptionKey: string;
	offerDefinitionId: number;
	customerId: number;
	offerPlayerInfo: OfferPlayerInfo;
	offerFreebetId: number;
	validTo: number;
	freebetStake: number;
	offerType: 'SPORT_BET_FREEBET';
	offerStatus: 'OFFER_EXPIRED' | 'OFFER_COMPLETED_AND_CLOSED';
	betSlipId?: number | null;
}

export interface FreebetResponseDto {
	active: ActiveFreebetDto[];
	history: HistoricalFreebetDto[];
}

export interface BonusAndFreebetResponseDto {
	bonusList: BonusListDto;
	freebetList: FreebetResponseDto;
}

export interface BonusWithdrawalResponseDto {
	offerDefinitionId: number;
	offerId: number;
	offerName: string;
	offerType: 'WAGER_BONUS' | 'SPORT_BET_FREEBET' | 'TAX_FACTOR_PROMOTION' | 'SPORT_BET_CASHBACK';
	value: number;
}

export interface CancelWithdrawalOffersResponseDto {
	bonusList: BonusWithdrawalResponseDto[];
}

export interface PromotionLeader {
	place: number;
	customerId: number;
	loginName: string;
	amount: number;
	points: number;
	prize: string;
	referenceType: number;
	referenceId: string[];
}

export interface PromotionLeadersResponse {
	pageSize: number;
	totalRows: number;
	code: number;
	items: PromotionLeader[];
	totalPages: number;
	success: boolean;
	offset: number;
	limit: number;
}

export interface DocumentTypesResponseDataDto {
	test: boolean;
}

export interface DocumentTypesResponseDto {
	documentTypes: DocumentTypesResponseDataDto;
}

export interface ActivePaymentProviderLimitsDto {
	minAmount?: number | null;
	transferNumberLimitType?: number | null;
	transferNumberLimitAttempts?: number | null;
	maxTotalAmountOnAttempts?: number | null;
	freeFeeAttemptsInPeriod?: number | null;
	freeFeePeriodType?: number | null;
	fee?: number | null;
}

export interface ActivePaymentProviderAdminSettingsDto {
	reverseToRequestedFromScs: boolean;
	decline: boolean;
	payout: boolean;
	reverseToRequestedFromIpr: boolean;
	reverseToRequestedFromSus: boolean;
}

export interface ActivePaymentProviderSettingsDto {
	firstDeposit?: boolean | null;
	amounts?: string[] | null;
	paymentCurrencies?: string[] | null;
	mustBeLogged?: boolean | null;
	order?: number | null;
	visible?: boolean | null;
	isParent?: boolean | null;
	parentId?: number | null;
	adminConsole?: ActivePaymentProviderAdminSettingsDto | null;
}

export interface ActivePaymentProviderDto {
	active: boolean;
	currencies: string[];
	isDeposit: boolean;
	limits?: ActivePaymentProviderLimitsDto | null;
	providerId: number;
	providerName: string;
	settings: ActivePaymentProviderSettingsDto;
}

export interface ActiveProvidersResponseDto {
	providers: ActivePaymentProviderDto[];
}

export interface PayoutResponsePaymentInfoDto {
	balanceAfterTransaction: number;
	isAreaManagerTransaction: boolean;
	isBettingShopTransaction: boolean;
	isFirstTransaction: boolean;
	manuallyFinished: boolean;
	responseCode: number;
	success: boolean;
	transactionAmount: number;
	transactionCurrency: string;
	transactionId: number;
	balanceTypeId?: string | null;
	externalRequestUrl?: string | null;
	isAgentConsoleTransaction?: boolean | null;
	isOnlineShopTransaction?: boolean | null;
	remoteTransactionAmount?: number | null;
	remoteTransactionCurrency?: string | null;
	remoteTransactionId?: string | null;
	remoteResponseCode?: string | null;
	remoteResponseText?: string | null;
	responseText?: string | null;
}

export interface PayoutResponseDto {
	paymentInfo: PayoutResponsePaymentInfoDto;
}

export interface DoPayoutBodyDto {
	/** amount to payout */
	amount: string;
	/** amount currency */
	amountCurrency: string;
}

export interface DepositResponsePaymentInfoDto {
	balanceAfterTransaction: number;
	isAreaManagerTransaction: boolean;
	isBettingShopTransaction: boolean;
	isFirstTransaction: boolean;
	manuallyFinished: boolean;
	responseCode: number;
	success: boolean;
	transactionAmount: number;
	transactionCurrency: string;
	transactionId: number;
	balanceTypeId?: string | null;
	externalRequestUrl?: string | null;
	isAgentConsoleTransaction?: boolean | null;
	isOnlineShopTransaction?: boolean | null;
	remoteTransactionAmount?: number | null;
	remoteTransactionCurrency?: string | null;
	remoteTransactionId?: string | null;
	remoteResponseCode?: string | null;
	remoteResponseText?: string | null;
	responseText?: string | null;
}

export interface DepositResponseDto {
	paymentInfo: DepositResponsePaymentInfoDto;
}

export interface DepositCustomerDetailsDto {
	/** customer email */
	email: string;
}

export interface DoDepositBodyDto {
	/** provider id */
	providerId: 232 | 233 | 274;
	/** amount to payout */
	transactionAmount: string;
	/** amount currency */
	transactionCurrency: string;
	/** callback url for payment provider to call after payment success */
	callbackUrl: string;
	/** customer details */
	customer: DepositCustomerDetailsDto;
	/** blik code if user selected blik payment */
	voucherCode?: string;
	/** additional data to pass */
	additionalData?: string;
	/** bank id if user selected bank payment */
	accountId?: number;
}

export interface InitializePaymentResponseInitDataDto {
	isAreaManagerTransaction: boolean;
	isBettingShopTransaction: boolean;
	manuallyFinished: boolean;
	responseCode?: string | null;
	success: boolean;
	transactionAmount: number;
	transactionCurrency: string;
	responseText?: string | null;
	providerId: number;
}

export interface InitializePaymentPromotionDataDto {
	promotionId: string;
	promotionName: string;
	/** @format date-time */
	dateTill: string | null;
	summary?: string;
	parameters: PromotionParameterDto[];
}

export interface InitializePaymentResponseDto {
	initData: InitializePaymentResponseInitDataDto;
	promotion: InitializePaymentPromotionDataDto;
}

export interface SecurityDto {
	/** password of user */
	securityToken: string;
}

export interface InitializePaymentBodyDto {
	/** provider id of payment provider */
	providerId: number;
	/** how much amount want to transact */
	transactionAmount: string;
	/** in which currency want to transaction */
	transactionCurrency: string;
	/** security related details of user */
	security: SecurityDto;
}

export interface ProcessPaymentResponsePaymentInfoDto {
	manuallyFinished: boolean;
	isAreaManagerTransaction: boolean;
	transactionId: number;
	transactionAmount: number;
	transactionCurrency: string;
	balanceAfterTransaction: number;
	isBettingShopTransaction: boolean;
	success: boolean;
	responseCode: number;
	responseText?: string | null;
	isFirstTransaction: boolean;
	remoteTransactionAmount?: number | null;
	remoteTransactionId?: string | null;
	remoteResponseCode?: string | null;
	remoteResponseText?: string | null;
	remoteTransactionCurrency?: string | null;
	isOnlineShopTransaction?: boolean | null;
	isAgentConsoleTransaction?: boolean | null;
	externalRequestUrl?: string | null;
	balanceTypeId?: string | null;
}

export interface ProcessPaymentResponseDto {
	paymentInfo: ProcessPaymentResponsePaymentInfoDto;
}

export interface BankDataDto {
	/** bank account number of user on which transaction will happen */
	accountNumber: string;
	/** decide action for this transaction, -1 is for payout */
	actionOnAccount: number;
	/** account id given by SB betting to this user */
	accountId: number;
	/** bank id from which this transaction amount deduct */
	bankId: number;
	/** account type given by sb betting */
	accountType: number;
	/** additional information in JSON format */
	additionalInformationsJSON: object;
}

export interface ProcessPaymentBodyDto {
	/** provider id of payment provider */
	providerId: number;
	/** how much amount want to transact */
	transactionAmount: string;
	/** in which currency want to transaction */
	transactionCurrency: string;
	/** security related details of user */
	security: SecurityDto;
	/** bank details to process payment */
	bank: BankDataDto;
	/** bonus code if applied */
	bonusCode?: string;
}

export interface StartTransferResponseDto {
	isTransfer: boolean;
}

export interface CustomerBankDetailsResponseDetailsDto {
	accountId: number;
	accountNumber: string;
	accountOwnerName?: string | null;
	accountType: number;
	bankId: number;
	bankName: string;
}

export interface CustomerBankDetailsResponseDto {
	bankDetails: CustomerBankDetailsResponseDetailsDto;
}

export interface BankDto {
	additionalInformationsJSON: string;
	bankId: number;
	bankImageUrl: string;
	bankName: string;
}

export interface BanksResponseDto {
	banks: BankDto[];
}

export interface SavedCreditCardDetailsResponseDto {
	cardNumber: string;
	cardType: string;
	creditCardId: number;
	customerId: number;
	defaultCard: boolean;
	expirationDate: string;
	fullCardHolderName: string;
	last4Digits: string;
	paymentProvider: string;
}

export interface PaymentConfigResponseMappingPayoutDto {
	bankPayout: number;
}

export interface PaymentConfigResponseMappingDepositDto {
	bank: number;
	blik: number;
	card: number;
}

export interface PaymentConfigResponseMappingDto {
	payout: PaymentConfigResponseMappingPayoutDto;
	deposit: PaymentConfigResponseMappingDepositDto;
}

export interface PaymentConfigResponseDto {
	banks: BankDto[];
	providers: ActivePaymentProviderDto[];
	providersMapping: PaymentConfigResponseMappingDto;
}

export interface CreditCardVisibilityDto {
	activeCard: boolean;
	creditCardId: number;
}

export interface CardDto {
	fullCardHolderName: string;
	defaultCard: boolean;
}

export interface PaymentVerifyBodyDto {
	transactionId: string;
	providerId: number;
	transactionCurrency: string;
	transactionAmount: number;
	card: CardDto;
}

export interface AppVersionDto {
	/**
	 * The platform from which the app is connecting. Supported platforms could include iOS, Android, Web, etc.
	 * @example "Android"
	 */
	platform: 'android' | 'ios';
	/**
	 * The current version of the application in the format "major.minor.patch" (e.g., "1.21.31"). This should match the versioning scheme defined in the system.
	 * @example "1.21.31"
	 */
	version: string;
}

export interface AppVersionResponseDto {
	/**
	 * Status of the response,  "current" / "optional" / "force-update".
	 * @example "success"
	 */
	status: string;
	/**
	 * Message that provides further details about the application version.
	 * @example "Jest nowa wersja aplikacji, zaktualizuj."
	 */
	message: string;
	/**
	 * URL where the application can be accessed or downloaded.
	 * @example "https://www.betfan.pl/app"
	 */
	app_url: string;
}

export interface TvbetResponseDto {
	tvbetFrame: string;
}

export interface StatscoreResponseDto {
	/** @example {"12766504":4568119,"12779965":4694907} */
	statScoreResponse: Record<string, number>;
}

export interface StatscoreEventResponseDto {
	statscoreEventId: number;
}

export interface BetradarLiveStreamResponseDto {
	betradarStreamUrl: string;
}

export interface SisLiveStreamResponseDto {
	sisStreamUrl: string;
}

export interface ParticipantDto {
	/**
	 * Participant number.
	 * @example 1
	 */
	number: number;
	/**
	 * Name of the participant.
	 * @example "John Doe"
	 */
	participantName: string;
	/**
	 * Unique identifier of the participant (optional).
	 * @example 101
	 */
	participantId?: number;
}

export interface GameDto {
	/**
	 * Unique identifier of the game.
	 * @example 123
	 */
	gameId: number;
	/**
	 * Name of the game.
	 * @example "Football Match"
	 */
	gameName: string;
	/**
	 * Type of the game.
	 * @example 1
	 */
	gameType: number;
	/** List of outcomes for the game. */
	outcomes: OutcomeDto[];
}

export interface EventDto {
	/**
	 * Unique identifier of the event.
	 * @example 12345
	 */
	eventId: number;
	/**
	 * Hash value of the event.
	 * @example "abc123"
	 */
	eventHash: string;
	/**
	 * Start time of the event.
	 * @example "2024-01-01T12:00:00.000Z"
	 */
	eventStart: number;
	/**
	 * Name of the event.
	 * @example "Champions League Final"
	 */
	eventName: string;
	/**
	 * Identifier for the sport.
	 * @example 1
	 */
	sportId: number;
	/**
	 * Parent category identifier.
	 * @example 10
	 */
	parentId: number;
	/**
	 * Category identifier of the event.
	 * @example 2
	 */
	categoryId: number;
	/**
	 * Name of the category.
	 * @example "Football"
	 */
	categoryName: string;
	/**
	 * Name of the sport.
	 * @example "Soccer"
	 */
	sportName: string;
	/**
	 * Name of the parent category.
	 * @example "Premier League"
	 */
	parentName: string;
	/**
	 * Indicates whether the event is live.
	 * @example true
	 */
	live: boolean;
	/**
	 * Indicates whether the event has finished.
	 * @example false
	 */
	finished: boolean;
	/**
	 * Indicates whether the event is deleted.
	 * @example false
	 */
	deleted: boolean;
	/**
	 * Partial scores of the event.
	 * @example "1-0"
	 */
	partialScores: string;
	/** List of participants in the event. */
	participants: ParticipantDto[];
	/** List of games associated with the event. */
	games: GameDto[];
	/** Link to the event. */
	eventUrl: string;
}

export interface EventCategoryDataDto {
	/**
	 * Unique identifier for the category.
	 * @example 1
	 */
	categoryId: number;
	/**
	 * Name of the category.
	 * @example "Premier League"
	 */
	categoryName: string;
	/**
	 * Level of the category in the hierarchy.
	 * @example 2
	 */
	level: number;
	/**
	 * Parent category identifier.
	 * @example 0
	 */
	parentCategory: number;
	/**
	 * Identifier of the associated sport.
	 * @example 1
	 */
	sportId: number;
	/**
	 * Name of the parent category.
	 * @example "Football"
	 */
	parentName: string;
	/**
	 * Name of the sport associated with the category.
	 * @example "Soccer"
	 */
	sportName: string;
}

export interface EventCategoryDto {
	/**
	 * Category identifier.
	 * @example 5
	 */
	categoryId: number;
	/**
	 * Parent category identifier.
	 * @example 1
	 */
	parentCategoryId: number;
	/** Additional data for the event category. */
	categoryData: EventCategoryDataDto[];
}

export interface VirtualRunningSportsResponseSportDto {
	currentEventId: number;
	currentRemoteId: number;
	sportType: string;
	upcomingEventId: number;
	upcomingRemoteId: number;
	upcomingTime: number;
}

export interface VirtualRunningSportsResponseDto {
	sports: VirtualRunningSportsResponseSportDto[];
}

export interface GenerateUofOutcomesResponseOutcomeDto {
	eventId: number;
	eventName: string;
	gameId: number;
	gameName: string;
	gameStatus: number;
	outcomeId: number;
	outcomeName: string;
	outcomeOdds: number;
	outcomeStatus: number;
	virtualType: string;
	selectionKey: string;
	eventType: number;
	sportId: number;
	parentId: number;
	categoryId: number;
	sportName: string;
	parentName: string;
	categoryName: string;
}

export interface GenerateUofOutcomesResponseDto {
	outcomes: GenerateUofOutcomesResponseOutcomeDto[];
}

export interface UofOutcomesDto {
	/** match id of sport event */
	remoteEventId: number;
	/** unified market id of event */
	remoteGameId: number;
	/** unified outcome id of event */
	remoteOutcomeType: string;
	/** selection key of event */
	selectionKey: string;
}

export interface GenerateUofOutcomesBodyDto {
	/** virtuals sports outcomes array */
	brOutcomes: UofOutcomesDto[];
}

export interface KuponKibicaResponseExtrasDto {
	liveScoreProEventId?: number | null;
}

export interface KuponKibicaTransactionDetailsDto {
	cat3DisplayedAs: string;
	categoryLev2: number;
	categoryLev3: number;
	categoryName1: string;
	categoryName2: string;
	categoryName3: string;
	eventId: number;
	eventName: string;
	eventStart: number;
	eventType: number;
	gameId: number;
	gameName: string;
	gameTypeId: number;
	isEsport: boolean;
	odds: number;
	outcomeId: number;
	outcomeName: string;
	outcomeResult: string;
	remoteId: number | null;
	score: string;
	sportId: number;
	statusCode: number;
	argument?: number | null;
	banker?: boolean | null;
	blockId?: number | null;
	brLiveEventIdRunningForPrematch?: number | null;
	changeType?: string | null;
	earlyPayoutAvailable?: boolean | null;
	extras?: KuponKibicaResponseExtrasDto | null;
	gameInfo?: Record<string, string>;
	liveEventId?: number | null;
	modifiedOn?: number | null;
	participants?: string | null;
	virtualType?: string | null;
}

export interface KuponKibicaResponseDto {
	transactionId: number;
	regDate: number;
	stake: number;
	transactionType: number;
	slipType: number;
	confirmedReturn?: number | null;
	possibleReturn?: number | null;
	bonus?: number | null;
	totalWinning?: number | null;
	statusCode: number;
	transactionsCount: number;
	decidedDate?: number | null;
	tax: number;
	taxConfirmed: number;
	regMethod: number;
	isFreebet?: boolean | null;
	totalOdds: number;
	totalOddsWithoutSalesTaxFactor: number;
	showCombinationDetails?: boolean | null;
	cashoutable: boolean;
	transactionDetails: KuponKibicaTransactionDetailsDto[];
}

export interface Avatar {
	/** Unique identifier for the avatar. */
	avatarId: number;
	/** URL to the avatar image (optional). */
	avatarUrl?: string;
	/** File name of an avatar image (optional). */
	avatarFile?: string;
	/** Background color for the avatar (optional). */
	backgroundColor?: string;
	/** Font color for the avatar (optional). */
	fontColor?: string;
}

export interface AvatarDefaultCategory {
	/** Unique identifier for the avatar category. */
	categoryId: number;
	/** Name of the avatar category. */
	categoryName: string;
	/** Flag indicating whether the category is newly created. */
	isNew: boolean;
	/** A brief tagline describing the category. */
	categoryTagline: string;
	/** List of avatars that belong to this category. */
	avatars: Avatar[];
}

export interface AvatarCustomCategory {
	/** Unique identifier for the custom avatar category. */
	categoryId: number;
	/** Name of the custom avatar category. */
	categoryName: string;
	/** A brief tagline describing the custom category. */
	categoryTagline: string;
	/** Flag indicating whether the category is newly created. */
	isNew: boolean;
	/** List of custom avatars in this category (optional). */
	avatars?: AvatarImage[];
}

export interface AvatarsResponse {
	/** The default avatar category with associated avatars. */
	default: AvatarDefaultCategory;
	/** Optional custom avatar categories. */
	custom?: AvatarCustomCategory[];
	/** Optional list of collected avatar images. */
	collected?: AvatarImage[];
}

export interface SaveProfileResponseDto {
	playerId: number;
	playerEmail: string;
	profile: PlayersProfileData;
}

export interface PutPlayerProfileDto {
	avatarId?: number;
	defaultStakes?: string[];
	defaultPaymentMethod?: string;
	lastDepositMethod?: string;
	lastDepositBankId?: number;
	collapsedMarkets?: CollapsedMarketsDto;
}

export interface ObservedDto {
	/** Unique identifier for the observed entity. */
	observedId: number;
	/** Type of the observed entity. */
	observedType: 'category1' | 'category2' | 'category3' | 'participant';
}

export type HelpStep = object;

export interface DailyChallengeModel {
	/** Title of the challenge. */
	challenge_title: string;
	/** Description of the challenge. */
	challenge_description: string;
	/**
	 * Numeric identifier of the challenge.
	 * @example 1
	 */
	challenge_id: number;
	/**
	 * Identifier for the sport category.
	 * @example 5
	 */
	sport_category: number;
	/**
	 * Name of the sport associated with the challenge.
	 * @example "Football"
	 */
	sport_name: string;
	/**
	 * Icon ID representing the country for the challenge.
	 * @example 1
	 */
	country_icon: number;
	/**
	 * Minimum stake required for the challenge.
	 * @example "10"
	 */
	minimum_stake: string;
	/**
	 * Number of events associated with the challenge.
	 * @example "3"
	 */
	event_count: string;
	/**
	 * Minimum odds required to qualify for the challenge.
	 * @example "2.0"
	 */
	minimum_odds: string;
	/**
	 * Value of the free bet awarded upon completion of the challenge.
	 * @example 100
	 */
	freebet_value: number;
	/**
	 * Description of the category associated with the challenge.
	 * @example "Premier League Specials"
	 */
	category_description: string;
	/**
	 * Start date and time for the challenge.
	 * @format date-time
	 * @example "2024-01-01T00:00:00.000Z"
	 */
	from: string;
	/**
	 * End date and time for the challenge.
	 * @format date-time
	 * @example "2024-01-07T23:59:59.000Z"
	 */
	till: string;
	/**
	 * Indicates whether the challenge is active.
	 * @example true
	 */
	is_active: boolean;
	/**
	 * Alternative text for the back image of the card.
	 * @example "Challenge Back Image"
	 */
	card_back_alt: string;
	/** URL for the back image of the card. */
	card_back_image: string;
	/**
	 * Alternative text for the front image of the card.
	 * @example "Challenge Front Image"
	 */
	card_front_alt: string;
	/** URL for the front image of the card. */
	card_front_image: string;
	/** Title of the help section. */
	help_title: string;
	/** Subtitle of the help section. */
	help_subtitle: string;
	/** Text for the help section button. */
	help_button_text: string;
	/** Steps for completing the challenge. */
	help_steps: HelpStep[];
}

export interface ActionData {
	bet_selected_1: number;
	bet_selected_2: number;
	event_category_1: number;
	event_category_2: number;
	event_count: number;
	event_id: number;
	event_type: 'live' | 'prematch';
	odds: number;
	origin_url: string;
	participants: string;
}

export interface Actions {
	/** Timestamp of user'sa action */
	action_at: number;
	/** Unique action id for deduplication */
	action_id: string;
	/** Type of the action */
	action_type: string;
	/** Action detailed data */
	action_data: ActionData;
}

export interface AnalyticsObject {
	/** unique session id of the gathered data  */
	sessionId: string;
	/** user id data is being gathered for */
	userId: number;
	/** session token of the user data is being gathered for */
	userOddsSession: string;
	/** Action being taken by the user */
	action: Actions[];
}

export interface FinishRegistrationDto {
	promoCode: string;
	/** source of income selected by user */
	fundSource: string;
	/** other fund source if fundSource == 100 */
	otherFundSource: string;
	password: string;
	/**
	 * user platform
	 * @example ["desktop","mobile"]
	 */
	channel: string;
	/**
	 * user browser
	 * @example ["microsoft","safari"]
	 */
	browser: string;
	/**
	 * user browser
	 * @example ["desktop","ipad"]
	 */
	device: string;
	affiliateCode?: string;
	/** fetch it from google analytics cookie -> __utmzz */
	clickId?: string;
	/**
	 * email address
	 * @example ["john@example.com"]
	 */
	email: string;
	/**
	 * phone number
	 * @example ["570096221"]
	 */
	phoneNum: string;
	/**
	 * SMS code for phone number verification
	 * @example ["1234"]
	 */
	smsCode: string;
	/**
	 * bank account number (IBAN without leading PL)
	 * @example ["69 1140 3002 0000 0000 7486 4917"]
	 */
	bankAccountNumber: string;
	/**
	 * country of birth
	 * @example ["PL"]
	 */
	countryOfBirth: string;
	/**
	 * address - city
	 * @example ["Warszawa"]
	 */
	city: string;
	/**
	 * address - postal code
	 * @example ["01-424"]
	 */
	postalCode: string;
	/**
	 * address - street
	 * @example ["Prosta"]
	 */
	street: string;
	/**
	 * address - building number
	 * @example ["12"]
	 */
	buildingNumber: string;
	/**
	 * address - apartment number
	 * @example ["34"]
	 */
	apartmentNumber: string;
	/**
	 * if the user is a PEP
	 * @example [true,false]
	 */
	isPEP: boolean;
}

export interface FinishVerificationDto {
	/** source of income selected by user */
	fundSource: string;
	/** other fund source if fundSource == 100 */
	otherFundSource: string;
	/**
	 * bank account number (IBAN without leading PL)
	 * @example ["69 1140 3002 0000 0000 7486 4917"]
	 */
	bankAccountNumber: string;
	/**
	 * address - city
	 * @example ["Warszawa"]
	 */
	city: string;
	/**
	 * address - postal code
	 * @example ["01-424"]
	 */
	postalCode: string;
	/**
	 * address - street
	 * @example ["Prosta"]
	 */
	street: string;
	/**
	 * address - building number
	 * @example ["12"]
	 */
	buildingNumber: string;
	/**
	 * address - apartment number
	 * @example ["34"]
	 */
	apartmentNumber: string;
	/**
	 * if the user is a PEP
	 * @example [true,false]
	 */
	isPEP: boolean;
}

export enum EventStartFilterType {
	Time = 'time',
	Date = 'date',
}

export interface EventStartFilterResponse {
	type: EventStartFilterType;
	label: string;
	value: string;
}

export interface AvailableFiltersResponse {
	availableFilters: EventStartFilterResponse[];
}

export namespace Livebetting {
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningGames
	 * @summary to get live running games
	 * @request GET:/api/v1/livebetting/running/games/{gameClass}
	 */
	export namespace GetRunningGames {
		export type RequestParams = {
			/**
			 * choose which live games to query
			 * @default "major"
			 */
			gameClass: 'main' | 'major' | 'all';
		};
		export type RequestQuery = {
			/** whether game types codes needed or not */
			getGameTypeCodes: boolean;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningGamesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRefreshOdds
	 * @summary to get latest odds of outcomes
	 * @request GET:/api/v1/livebetting/running/refresh-odds
	 */
	export namespace GetRefreshOdds {
		export type RequestParams = {};
		export type RequestQuery = {
			/** outcome ids to fetch odds */
			outcomeIds: string[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RefreshOddsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEvents
	 * @summary to live events based on event id
	 * @request GET:/api/v1/livebetting/running/events
	 */
	export namespace GetRunningEvents {
		export type RequestParams = {};
		export type RequestQuery = {
			/** whether game types codes needed or not */
			getGameTypeCodes: boolean;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEventsBySport
	 * @summary to get live running events of sports
	 * @request GET:/api/v1/livebetting/running/events/sports/{sportIds}
	 */
	export namespace GetRunningEventsBySport {
		export type RequestParams = {
			/** sport ids of which events to fetch */
			sportIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEventsBySportResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEventsEsports
	 * @summary to get live running esports events
	 * @request GET:/api/v1/livebetting/running/events/esports/game-class/{gameClass}
	 */
	export namespace GetRunningEventsEsports {
		export type RequestParams = {
			/**
			 * choose which live games to query
			 * @default "major"
			 */
			gameClass: 'main' | 'major' | 'all';
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEsportEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * @description to fetch esport event details
	 * @tags Livebetting
	 * @name GetRunningEsportEvent
	 * @request GET:/api/v1/livebetting/running/events/esports/{eventIds}
	 */
	export namespace GetRunningEsportEvent {
		export type RequestParams = {
			/** events ids */
			eventIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEsportEventsByIdResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEsportEvents
	 * @summary to get live running esports events
	 * @request GET:/api/v1/livebetting/running/events/esports
	 */
	export namespace GetRunningEsportEvents {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEsportEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetAllRunningEsportEvents
	 * @summary to get All running esports events
	 * @request GET:/api/v1/livebetting/running/events/all
	 */
	export namespace GetAllRunningEsportEvents {
		export type RequestParams = {};
		export type RequestQuery = {
			/** search based on given number of hours or days from now */
			filter?: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: GroupedRunningEsportEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEventsByIds
	 * @summary to live events based on event id
	 * @request GET:/api/v1/livebetting/running/events/{eventIds}
	 */
	export namespace GetRunningEventsByIds {
		export type RequestParams = {
			/** events ids */
			eventIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEventsByIdResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningSportsCategories
	 * @summary to get live running sports categories
	 * @request GET:/api/v1/livebetting/running/categories/sports
	 */
	export namespace GetRunningSportsCategories {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningSportCategoriesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetRunningEventsByCategoriesForDropdown
	 * @summary to get live running events by categories
	 * @request GET:/api/v1/livebetting/running/levels/{level}/categories/{ids}/events/dropdown
	 */
	export namespace GetRunningEventsByCategoriesForDropdown {
		export type RequestParams = {
			/** categories ids to search events */
			ids: string[];
			/** category level to search event */
			level: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEventsDropdownResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetEventsCount
	 * @summary to get live running events count
	 * @request GET:/api/v1/livebetting/running/event-count
	 */
	export namespace GetEventsCount {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RunningEventCountResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Livebetting
	 * @name GetPopularLiveEventsByIds
	 * @request GET:/api/v1/livebetting/running/popular
	 */
	export namespace GetPopularLiveEventsByIds {
		export type RequestParams = {};
		export type RequestQuery = {
			/** categoryIds */
			categoryIds: string[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
}

export namespace Market {
	/**
	 * No description
	 * @tags Market
	 * @name GetGameOutcomesData
	 * @summary list outcome id list
	 * @request GET:/api/v1/market/game-outcomes-data/{outcomeIds}
	 */
	export namespace GetGameOutcomesData {
		export type RequestParams = {
			/**
			 * list of outcome ids
			 * @format csv
			 */
			outcomeIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: GetGameOutcomeResponseDTO;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetBestsellerGames
	 * @summary to get best sellers events
	 * @request GET:/api/v1/market/events/bestsellers
	 */
	export namespace GetBestsellerGames {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BestsellerEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEsportsEvents
	 * @summary to get best sellers esports events
	 * @request GET:/api/v1/market/events/esports
	 */
	export namespace GetEsportsEvents {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BestsellerEsportEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventsByGamesIds
	 * @summary to get events by games ids
	 * @request GET:/api/v1/market/events/games/{gameIds}
	 */
	export namespace GetEventsByGamesIds {
		export type RequestParams = {
			/**
			 * list of games ids to find
			 * @format csv
			 */
			gameIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventsByGameIdsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventsByQuickSearch
	 * @summary to search event based on phrase
	 * @request POST:/api/v1/market/events/quick-search
	 */
	export namespace GetEventsByQuickSearch {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = EventQuickSearchBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventQuickSearchResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventsStatscore
	 * @summary to get mapping of event id to statscore id
	 * @request GET:/api/v1/market/events/statscore
	 */
	export namespace GetEventsStatscore {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventsStatscoreResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetTodaysEvents
	 * @summary to get today events list
	 * @request GET:/api/v1/market/events/today-offers/{todayDate}
	 */
	export namespace GetTodaysEvents {
		export type RequestParams = {
			/**
			 * todays client date
			 * @format yyyy-mm-dd
			 */
			todayDate: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: TodayEventsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventDetails
	 * @summary to fetch event details which is not started yet
	 * @request GET:/api/v1/market/events/{eventId}
	 */
	export namespace GetEventDetails {
		export type RequestParams = {
			/** event id used to fetch event */
			eventId: string;
		};
		export type RequestQuery = {
			/** sport Id to filter SBB query */
			sportId: number;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventByIdResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetCategories
	 * @summary to get categories, used in discipline sports
	 * @request GET:/api/v1/market/categories
	 */
	export namespace GetCategories {
		export type RequestParams = {};
		export type RequestQuery = {
			/** search based on given number of hours or days from now */
			filter?: string;
			/** list categories only found in this sports */
			sportsId?: string;
			/** list categories only found in this league/parent category */
			parentId?: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CategoriesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetAllCategories
	 * @summary to get all categories
	 * @request GET:/api/v1/market/categories/all
	 */
	export namespace GetAllCategories {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AllCategoriesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetCategoriesDescriptions
	 * @summary to get categories descriptions
	 * @request GET:/api/v1/market/categories/descriptions
	 */
	export namespace GetCategoriesDescriptions {
		export type RequestParams = {};
		export type RequestQuery = {
			/**
			 * list of categories ids to find their descriptions
			 * @format csv
			 */
			categoryIds: string[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CategoriesDescriptionsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetCategoriesFlag
	 * @summary to get categories flag
	 * @request GET:/api/v1/market/categories/flags
	 */
	export namespace GetCategoriesFlag {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CategoriesFlagsResponse;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventsByCategories
	 * @summary to fetch events by categories
	 * @request GET:/api/v1/market/categories/{categoryId}/events
	 */
	export namespace GetEventsByCategories {
		export type RequestParams = {
			categoryId: number;
		};
		export type RequestQuery = {
			/** search based on given number of hours or days from now */
			filter?: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventsByCategoriesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Market
	 * @name GetEventsByCategoryForDropdown
	 * @summary to fetch event of category for display them in dropdown breadcomb
	 * @request GET:/api/v1/market/categories/{categoryId}/events/dropdown
	 */
	export namespace GetEventsByCategoryForDropdown {
		export type RequestParams = {
			/** category ids for filter */
			categoryId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EventsByCategoryForDropdownResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Customer {
	/**
	 * No description
	 * @tags Customer
	 * @name Login
	 * @summary to login
	 * @request POST:/api/v1/customer/session/login
	 * @secure
	 */
	export namespace Login {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = LoginDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: LoginResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name Logout
	 * @summary to logout
	 * @request DELETE:/api/v1/customer/session/logout
	 * @secure
	 */
	export namespace Logout {
		export type RequestParams = {};
		export type RequestQuery = {
			'device-code'?: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: LogoutResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name KeepAliveSession
	 * @summary to check whether user session alive or not
	 * @request GET:/api/v1/customer/session/keep-alive
	 * @secure
	 */
	export namespace KeepAliveSession {
		export type RequestParams = {};
		export type RequestQuery = {
			/** whether to keep-alive session or not */
			keepAlive: boolean;
			/**
			 * whether to need last stake amount of customer or not
			 * @default false
			 */
			needLastStake?: boolean;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: KeepAliveResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetSessionHistory
	 * @summary to get users sessions history
	 * @request GET:/api/v1/customer/session/history
	 * @secure
	 */
	export namespace GetSessionHistory {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in dd-mm-yyyy format */
			dateFrom?: string;
			/** end date for seach in dd-mm-yyyy format */
			dateTo?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * no of record to fetch
			 * @default 0
			 */
			offset: number;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SessionHistoryResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetAccountLimit
	 * @summary to users account limit
	 * @request GET:/api/v1/customer/account/limit
	 * @secure
	 */
	export namespace GetAccountLimit {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AccountLimitsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name UpdateAccountLimit
	 * @summary to update customer account limits
	 * @request PUT:/api/v1/customer/account/limit
	 * @secure
	 */
	export namespace UpdateAccountLimit {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = UpdateLimitBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AccountLimitsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetVip
	 * @summary To check if the customer is VIP
	 * @request GET:/api/v1/customer/segmentation/vip/level
	 * @secure
	 */
	export namespace GetVip {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: number | any[];
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetVerificationStatus
	 * @summary to verification status of user account and customer data
	 * @request GET:/api/v1/customer/account/verification-status
	 * @secure
	 */
	export namespace GetVerificationStatus {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: VerificationStatusResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name ChangePassword
	 * @summary to change password of user
	 * @request POST:/api/v1/customer/account/change-password
	 * @secure
	 */
	export namespace ChangePassword {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ChangePasswordDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ChangePasswordResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name PreRegister
	 * @summary to send SMS code to user mobile, pre-registration step
	 * @request POST:/api/v1/customer/account/pre-register
	 * @secure
	 */
	export namespace PreRegister {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = PreRegisterBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PreRegisterResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name Register
	 * @summary to complete registration
	 * @request POST:/api/v1/customer/account/register
	 * @secure
	 */
	export namespace Register {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = RegisterBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RegisterResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name CheckFieldValueUnique
	 * @summary to check given field and value is unique or not
	 * @request GET:/api/v1/customer/account/is-value-unique/{fieldName}/{fieldValue}
	 * @secure
	 */
	export namespace CheckFieldValueUnique {
		export type RequestParams = {
			/** field name to do search operation */
			fieldName: 'email' | 'personalIdentifier' | 'mobile' | 'document-number';
			/** field value to check unique */
			fieldValue: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: boolean;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name ActivateTemporaryAccount
	 * @summary to activate user account after verification
	 * @request POST:/api/v1/customer/account/activate-temporary-account
	 * @secure
	 */
	export namespace ActivateTemporaryAccount {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ActivateAccountBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: boolean;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name ResetPassword
	 * @summary to send reset password link
	 * @request POST:/api/v1/customer/account/reset-password
	 * @secure
	 */
	export namespace ResetPassword {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ResetPasswordDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: boolean;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name ResetPasswordVerify
	 * @summary to verify reset password
	 * @request POST:/api/v1/customer/account/reset-password-verify
	 * @secure
	 */
	export namespace ResetPasswordVerify {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ResetPasswordVerifyBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: boolean;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name CloseAccount
	 * @summary to close account
	 * @request POST:/api/v1/customer/account/close
	 * @secure
	 */
	export namespace CloseAccount {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CloseAccountResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name SelfExclusionAccount
	 * @summary to do self exclusion, use 1 day in body to suspend account
	 * @request POST:/api/v1/customer/account/self-exclusion
	 * @secure
	 */
	export namespace SelfExclusionAccount {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = UpdateSelfExclusionBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AccountLimitsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetAccountInformation
	 * @summary to get account information
	 * @request GET:/api/v1/customer/account/information
	 * @secure
	 */
	export namespace GetAccountInformation {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AccountInformationResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name FinishVerification
	 * @summary to request for verification to admin
	 * @request POST:/api/v1/customer/account/verification-finish
	 * @secure
	 */
	export namespace FinishVerification {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = FinishVerificationBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: FinishVerificationResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetApprovedSlips
	 * @summary To get approved slips data
	 * @request GET:/api/v1/customer/session/approved-slips
	 * @secure
	 */
	export namespace GetApprovedSlips {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ApprovedSlipsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetConsentTags
	 * @request GET:/api/v1/customer/account/consents
	 * @secure
	 */
	export namespace GetConsentTags {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ConsentTagResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name ChangeAccountConsent
	 * @request POST:/api/v1/customer/account/consents
	 * @secure
	 */
	export namespace ChangeAccountConsent {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ChangeConsentRequestDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ChangeConsentResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Customer
	 * @name GetPersonalData
	 * @request GET:/api/v1/customer/personal-data
	 * @secure
	 */
	export namespace GetPersonalData {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = PersonalDataResponseDto;
	}
}

export namespace Betting {
	/**
	 * No description
	 * @tags Betting
	 * @name CalculateTaxAndWinning
	 * @summary to calculate tax winning
	 * @request POST:/api/v1/betting/calculate-tax-winning/{registerMethod}
	 * @secure
	 */
	export namespace CalculateTaxAndWinning {
		export type RequestParams = {
			registerMethod: string;
		};
		export type RequestQuery = {};
		export type RequestBody = CalculateTaxWinningDto;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CalculateTaxAndWinningResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name PlaceBet
	 * @summary to place bet
	 * @request POST:/api/v1/betting/place-bet/{registerMethod}
	 * @secure
	 */
	export namespace PlaceBet {
		export type RequestParams = {
			registerMethod: string;
		};
		export type RequestQuery = {};
		export type RequestBody = CalculateTaxWinningDto;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PlaceBetResponseDto;
			  }
			| BetActionErrorResponseDto
			| PlaceBetResponseDto
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name ApproveBet
	 * @summary To be called by front-end if place-bet api gives error code 30
	 * @request POST:/api/v1/betting/approve-bet/{registerMethod}
	 * @secure
	 */
	export namespace ApproveBet {
		export type RequestParams = {
			registerMethod: string;
		};
		export type RequestQuery = {};
		export type RequestBody = CalculateTaxWinningDto;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ApproveBetResponseDto;
			  }
			| BetActionErrorResponseDto
			| ApproveBetResponseDto
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name FindBestOutcome
	 * @summary to find best outcome based on given filters
	 * @request GET:/api/v1/betting/outcomes
	 * @secure
	 */
	export namespace FindBestOutcome {
		export type RequestParams = {};
		export type RequestQuery = {
			/** minimum odds */
			fromOdds: string;
			/** maximum odds */
			toOdds: string;
			/** stake to put */
			stake: string;
			/** possible win */
			amount: string;
			/** search in the today events */
			today: boolean;
			/** search in the tomorrow events */
			tomorrow: boolean;
			/** search in the day after events */
			dayAfter: boolean;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: FindBestOutcomesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name ValidateCustomBets
	 * @summary to validate outcome for custom bet and find odds
	 * @request GET:/api/v1/betting/custom-bet/validate
	 */
	export namespace ValidateCustomBets {
		export type RequestParams = {};
		export type RequestQuery = {
			/** outcome ids for validate and find odds */
			outcomeIds: string[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ValidateCustomBetsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name CalculateCustomBets
	 * @summary to calculate/generate outcome for custom bet
	 * @request GET:/api/v1/betting/custom-bet/calculate
	 */
	export namespace CalculateCustomBets {
		export type RequestParams = {};
		export type RequestQuery = {
			/** outcome ids for calculate and generate new outcome */
			outcomeIds: string[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CalculateCustomBetsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Betting
	 * @name UpdateCustomBetOdds
	 * @summary to update custom bet odds
	 * @request GET:/api/v1/betting/custom-bet/update-odds/{outcomeId}
	 */
	export namespace UpdateCustomBetOdds {
		export type RequestParams = {
			outcomeId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: UpdateCustomBetOddsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Transaction {
	/**
	 * No description
	 * @tags Transaction
	 * @name GetTransactionsByDate
	 * @summary to get transactions by date filters, use for coupons history
	 * @request GET:/api/v1/transaction/by-date
	 * @secure
	 */
	export namespace GetTransactionsByDate {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in yyyy-mm-dd format */
			from?: string;
			/** end date for seach in yyyy-mm-dd format */
			to?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * no of record to fetch
			 * @default 0
			 */
			offset?: number;
			pageToken?: string;
			/** transaction types to filter */
			type: 'maxizysk' | 'won' | 'lost' | 'undecided' | 'all' | 'tvBet' | 'cashout';
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: TransactionByDateResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Transaction
	 * @name AnnotateTransaction
	 * @summary to give a trasansaction its custom name
	 * @request POST:/api/v1/transaction/annotate
	 * @secure
	 */
	export namespace AnnotateTransaction {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = AnnotateTransactionDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AnnotateTransactionDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Transaction
	 * @name GetNumberActiveBetslips
	 * @summary returns number of active betslips
	 * @request GET:/api/v1/transaction/number-active-betslips
	 * @secure
	 */
	export namespace GetNumberActiveBetslips {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example true */
			isError: true;
			/** @example 500 */
			code: number;
			message: string;
		};
	}
	/**
	 * No description
	 * @tags Transaction
	 * @name GetTransfersByDate
	 * @summary to get transfers by date filters, use to show user transfer activities
	 * @request GET:/api/v1/transaction/transfers/by-date
	 * @secure
	 */
	export namespace GetTransfersByDate {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in yyyy-mm-dd format */
			from?: string;
			/** end date for seach in yyyy-mm-dd format */
			to?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * no of record to fetch
			 * @default 0
			 */
			offset: number;
			/** which types of transfers needed */
			type: 'all' | 'payout' | 'deposit';
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: TransfersByDateResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Transaction
	 * @name CustomerHasBetPlacedWithin
	 * @summary to know whether customer has placed bet in given days or not
	 * @request GET:/api/v1/transaction/has-customer-placed-bet
	 * @secure
	 */
	export namespace CustomerHasBetPlacedWithin {
		export type RequestParams = {};
		export type RequestQuery = {
			/** no of days to account */
			days: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CustomerHasPlacedBetResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Transaction
	 * @name GetSettledSlipAfterLastLogin
	 * @summary to get settled slip data after last login
	 * @request GET:/api/v1/transaction/slips-settled-after-last-login
	 * @secure
	 */
	export namespace GetSettledSlipAfterLastLogin {
		export type RequestParams = {};
		export type RequestQuery = {
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * no of record to fetch
			 * @default 0
			 */
			offset: number;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SlipsSettledAfterLastLoginResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Cashout {
	/**
	 * No description
	 * @tags Cashout
	 * @name GetMaximumCashout
	 * @summary to view available cashout on given slip
	 * @request GET:/api/v1/cashout/max/{slipId}
	 * @secure
	 */
	export namespace GetMaximumCashout {
		export type RequestParams = {
			/** slip id to check max cashout amount */
			slipId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AvailableCashoutResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * @description NOTE: For live event call this api after calling prepare-live-countdown api and wait 10 seconds
	 * @tags Cashout
	 * @name DoCashout
	 * @summary to do cashout
	 * @request POST:/api/v1/cashout/full/{slipId}
	 * @secure
	 */
	export namespace DoCashout {
		export type RequestParams = {
			/** slip id to full cashout amount */
			slipId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = FullCashoutBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: FullCashoutResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * @description This api need to be call before live event cashout.
	 * @tags Cashout
	 * @name PrepareCashout
	 * @summary to be called by app before doing full cashout for live event
	 * @request POST:/api/v1/cashout/prepare-live-countdown/{slipId}
	 * @secure
	 */
	export namespace PrepareCashout {
		export type RequestParams = {
			/** slip id to full cashout amount */
			slipId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = PrepareCashoutBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PrepareLiveCountdownResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Cms {
	/**
	 * No description
	 * @tags Cms
	 * @name GetHotBanners
	 * @summary to get banners based on query parameters
	 * @request GET:/api/v1/cms/hot
	 */
	export namespace GetHotBanners {
		export type RequestParams = {};
		export type RequestQuery = {
			/** to get banners for logged users */
			forNonLogged?: boolean;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BannersResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetSubBanners
	 * @summary to get sub-banners
	 * @request GET:/api/v1/cms/sub-banners
	 */
	export namespace GetSubBanners {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: SubBannersResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetRegistrationBanners
	 * @summary to get registration banner list
	 * @request GET:/api/v1/cms/registration/banners
	 */
	export namespace GetRegistrationBanners {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: RegistrationBannersResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetEntertainments
	 * @summary to get entertainments based on categories and paginations
	 * @request GET:/api/v1/cms/entertainments
	 */
	export namespace GetEntertainments {
		export type RequestParams = {};
		export type RequestQuery = {
			/**
			 * no of records to fetch
			 * @default 20
			 */
			limit?: string;
			/**
			 * from which page to fetch
			 * @default 1
			 */
			pageNo?: string;
			/** category in which entertainment exist */
			category?: 'politics' | 'current' | 'sports';
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: EntertainmentsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetPlacements
	 * @summary to get placements based on components
	 * @request GET:/api/v1/cms/placements
	 */
	export namespace GetPlacements {
		export type RequestParams = {};
		export type RequestQuery = {
			/** screens in which placements exist */
			screen: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: PlacementsResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetRegulations
	 * @summary to get regulations list to use in footer
	 * @request GET:/api/v1/cms/regulations
	 */
	export namespace GetRegulations {
		export type RequestParams = {};
		export type RequestQuery = {
			/** which types of regulation needed */
			types: ('responsibleGaming' | 'promotions' | 'privacy' | 'privacyPolicy' | 'gdpr' | 'vip')[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: any;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetPromotions
	 * @summary to get promotions list
	 * @request GET:/api/v1/cms/promotions
	 */
	export namespace GetPromotions {
		export type RequestParams = {};
		export type RequestQuery = {
			/**
			 * no of records to fetch
			 * @default 20
			 */
			limit?: string;
			/**
			 * from which page to fetch
			 * @default 1
			 */
			pageNo?: string;
			/** types of promotion to fetch */
			type?: 'constant' | 'temporary';
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: PromotionsResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetPromotionById
	 * @summary to get promotion details
	 * @request GET:/api/v1/cms/promotions/{promotionSlug}
	 */
	export namespace GetPromotionById {
		export type RequestParams = {
			/** promotion name slug */
			promotionSlug: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: PromotionResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetRegistrationPromotions
	 * @summary to get registration promotions list
	 * @request GET:/api/v1/cms/registration-promotions
	 */
	export namespace GetRegistrationPromotions {
		export type RequestParams = {};
		export type RequestQuery = {
			code: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = RegistrationPromotionsResponseDto;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name PromotionSignup
	 * @request GET:/api/v1/cms/promotions/signup/{id}
	 * @secure
	 */
	export namespace PromotionSignup {
		export type RequestParams = {
			id: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetMessages
	 * @summary to get messages list
	 * @request GET:/api/v1/cms/messages
	 * @secure
	 */
	export namespace GetMessages {
		export type RequestParams = {};
		export type RequestQuery = {
			/**
			 * no of records to fetch
			 * @default 20
			 */
			limit?: string;
			/**
			 * from which page to fetch
			 * @default 1
			 */
			pageNo?: string;
			/**
			 * whether need to fetch current/active messages
			 * @default true
			 */
			current?: boolean;
			/**
			 * current user id
			 * @deprecated
			 */
			userId?: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name DeleteAllMessages
	 * @summary delete all messages
	 * @request DELETE:/api/v1/cms/messages
	 * @secure
	 */
	export namespace DeleteAllMessages {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetUnreadMessagesCount
	 * @summary to get unread messages count
	 * @request GET:/api/v1/cms/messages/unread
	 * @secure
	 */
	export namespace GetUnreadMessagesCount {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetMessageById
	 * @summary to get message details
	 * @request GET:/api/v1/cms/messages/{messageId}
	 * @secure
	 */
	export namespace GetMessageById {
		export type RequestParams = {
			messageId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name MarkMessageRead
	 * @summary to get message details
	 * @request PUT:/api/v1/cms/messages/{messageId}/read
	 * @secure
	 */
	export namespace MarkMessageRead {
		export type RequestParams = {
			messageId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name DeleteMessages
	 * @request DELETE:/api/v1/cms/messages/{messageIds}
	 * @secure
	 */
	export namespace DeleteMessages {
		export type RequestParams = {
			/** ids of messages to be deleted */
			messageIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetAppTranslations
	 * @summary to get app translation data
	 * @request GET:/api/v1/cms/app-translation
	 */
	export namespace GetAppTranslations {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: AppTranslationsResponseDto[];
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name MyBet
	 * @summary to request custom bet to bookmakers.
	 * @request POST:/api/v1/cms/my-bet
	 * @secure
	 */
	export namespace MyBet {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = MybetRequestDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetCustomPage
	 * @summary to get custom page detail
	 * @request GET:/api/v1/cms/custom-page/{slug}
	 */
	export namespace GetCustomPage {
		export type RequestParams = {
			slug: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: CustomPageResponseDataDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name SubmitPromoCode
	 * @summary to submit promo code
	 * @request POST:/api/v1/cms/promo-code
	 */
	export namespace SubmitPromoCode {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = PromoCodeRequestDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = CommonMessageResponseDto;
	}
	/**
	 * No description
	 * @tags Cms
	 * @name SportEventImage
	 * @request GET:/api/v1/cms/sport-event-image
	 */
	export namespace SportEventImage {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example false */
			isError: false;
			/** @example 200 */
			code: number;
			data: SportEventImageResponseDto;
		};
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetStores
	 * @request GET:/api/v1/cms/bet-stores
	 */
	export namespace GetStores {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: GetBetShopResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetSeo
	 * @request GET:/api/v1/cms/seo/{type}
	 */
	export namespace GetSeo {
		export type RequestParams = {
			type: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SeoResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetRegulation
	 * @request GET:/api/v1/cms/regulations/{type}
	 */
	export namespace GetRegulation {
		export type RequestParams = {
			type: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SeoResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetBetmatch
	 * @request GET:/api/v1/cms/betmatches
	 */
	export namespace GetBetmatch {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BetmatchResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Cms
	 * @name GetCategories
	 * @request GET:/api/v1/cms/categories
	 */
	export namespace GetCategories {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example true */
			isError: true;
			/** @example 500 */
			code: number;
			message: string;
		};
	}
}

export namespace Offers {
	/**
	 * No description
	 * @tags Offers
	 * @name GetBonusList
	 * @summary to get bonus list of user
	 * @request GET:/api/v1/offers/bonus
	 * @secure
	 */
	export namespace GetBonusList {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in dd-mm-yyyy format */
			from?: string;
			/** end date for seach in dd-mm-yyyy format */
			to?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * offset from where to start fetching records
			 * @default 0
			 */
			offset: number;
			/** which bonuses to fetch */
			status?: ('ACTIVE' | 'QUEUE' | 'HISTORY')[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BonusResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name ActivateBonus
	 * @summary to activate bonus
	 * @request POST:/api/v1/offers/bonus/activate/{queueId}
	 * @secure
	 */
	export namespace ActivateBonus {
		export type RequestParams = {
			/** offer queue id which used to identify bonus to activate */
			queueId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BonusResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name RejectBonus
	 * @summary to reject bonus
	 * @request POST:/api/v1/offers/bonus/reject/{queueId}
	 * @secure
	 */
	export namespace RejectBonus {
		export type RequestParams = {
			/** offer queue id which used to identify bonus to reject */
			queueId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BonusResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name CancelActiveBonus
	 * @summary to cancel active bonus
	 * @request DELETE:/api/v1/offers/bonus/{offerId}
	 * @secure
	 */
	export namespace CancelActiveBonus {
		export type RequestParams = {
			/** offer id of bonus which needs to cancel */
			offerId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CancelActiveBonusResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name GetFreebetList
	 * @summary to get freebet list of user
	 * @request GET:/api/v1/offers/freebet
	 * @secure
	 */
	export namespace GetFreebetList {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in dd-mm-yyyy format */
			from?: string;
			/** end date for seach in dd-mm-yyyy format */
			to?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * offset from where to start fetching records
			 * @default 0
			 */
			offset: number;
			/** freebet status to filter */
			status?: 'ACTIVE'[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: FreebetResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name GetBonusAndFreebetList
	 * @summary to get bonuses and freebets list of user
	 * @request GET:/api/v1/offers/bonus-and-freebets
	 * @secure
	 */
	export namespace GetBonusAndFreebetList {
		export type RequestParams = {};
		export type RequestQuery = {
			/** start date for seach in dd-mm-yyyy format */
			from?: string;
			/** end date for seach in dd-mm-yyyy format */
			to?: string;
			/**
			 * no of record to fetch
			 * @default 10
			 */
			limit: number;
			/**
			 * offset from where to start fetching records
			 * @default 0
			 */
			offset: number;
			/** which bonuses to fetch */
			status?: ('ACTIVE' | 'QUEUE' | 'HISTORY')[];
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BonusAndFreebetResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Offers
	 * @name GetCancelBonusList
	 * @summary to get potential list of bonus to cancel during withdrawal.
	 * @request GET:/api/v1/offers/cancel/withdrawal
	 * @secure
	 */
	export namespace GetCancelBonusList {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CancelWithdrawalOffersResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Promotions {
	/**
	 * No description
	 * @tags Promotions
	 * @name GetPromotionsLeaderboard
	 * @summary Get leaders for promotion by ID, with optional query params
	 * @request GET:/api/v1/promotions/leaderboard/{id}
	 */
	export namespace GetPromotionsLeaderboard {
		export type RequestParams = {
			/** Promotion ID */
			id: number;
		};
		export type RequestQuery = {
			/** Period for filtering - undocummented feature of SBB */
			period: number;
			/** Number of results per page */
			pageSize: number;
			/** Page number for pagination */
			pageNo?: number;
		};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PromotionLeadersResponse;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Documents {
	/**
	 * No description
	 * @tags Documents
	 * @name GetDocumentTypes
	 * @summary to get document types list
	 * @request GET:/api/v1/documents/types
	 */
	export namespace GetDocumentTypes {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: DocumentTypesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace Payments {
	/**
	 * No description
	 * @tags Payments
	 * @name GetActiveProviders
	 * @summary to get active providers list
	 * @request GET:/api/v1/payments/providers/active
	 */
	export namespace GetActiveProviders {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ActiveProvidersResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name DoPayout
	 * @summary to do payout from user balance
	 * @request POST:/api/v1/payments/payout
	 * @secure
	 */
	export namespace DoPayout {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = DoPayoutBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PayoutResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name DoDeposit
	 * @summary to do deposit to user balance
	 * @request POST:/api/v1/payments/deposit
	 * @secure
	 */
	export namespace DoDeposit {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = DoDepositBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: DepositResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name InitializePayment
	 * @summary to initialize payment
	 * @request POST:/api/v1/payments/initialize
	 * @secure
	 */
	export namespace InitializePayment {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = InitializePaymentBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: InitializePaymentResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name ValidateApplePay
	 * @summary to validate apple pay
	 * @request POST:/api/v1/payments/validate-apple-pay
	 * @secure
	 */
	export namespace ValidateApplePay {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = {
			/** @example true */
			isError: true;
			/** @example 500 */
			code: number;
			message: string;
		};
	}
	/**
	 * No description
	 * @tags Payments
	 * @name ProcessPayment
	 * @summary to process payment, final second step of payment process
	 * @request POST:/api/v1/payments/process
	 * @secure
	 */
	export namespace ProcessPayment {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ProcessPaymentBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: ProcessPaymentResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name StartTransfer
	 * @summary to start transfer, it is middle step between deposit and redirect to url
	 * @request POST:/api/v1/payments/start-transfer/{transactionId}
	 * @secure
	 */
	export namespace StartTransfer {
		export type RequestParams = {
			/** transaction id of which to start transfer */
			transactionId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: StartTransferResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name GetCustomerBankDetails
	 * @summary to get customer bank details
	 * @request GET:/api/v1/payments/customer-bank-details
	 * @secure
	 */
	export namespace GetCustomerBankDetails {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CustomerBankDetailsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name GetBanks
	 * @summary to get list of banks of provider
	 * @request GET:/api/v1/payments/banks/{providerId}
	 */
	export namespace GetBanks {
		export type RequestParams = {
			/** provider id of which to get banks list */
			providerId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BanksResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name GetSavedCreditCardDetails
	 * @summary to get details of a saved credit card
	 * @request GET:/api/v1/payments/credit-card/list/{providerId}
	 * @secure
	 */
	export namespace GetSavedCreditCardDetails {
		export type RequestParams = {
			/** provider id of which to get banks list */
			providerId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SavedCreditCardDetailsResponseDto[];
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name GetPaymentConfig
	 * @summary to get payment configurations
	 * @request GET:/api/v1/payments/config
	 */
	export namespace GetPaymentConfig {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PaymentConfigResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name SetCreditCardVisibility
	 * @summary to set a visibility of a saved credit card
	 * @request POST:/api/v1/payments/credit-card/update
	 * @secure
	 */
	export namespace SetCreditCardVisibility {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = CreditCardVisibilityDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: boolean;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Payments
	 * @name PaymentVerify
	 * @summary to save credit card info
	 * @request POST:/api/v1/payments/payment/verify
	 * @secure
	 */
	export namespace PaymentVerify {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = PaymentVerifyBodyDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Payments
	 * @name PaymentProviderStatus
	 * @summary see the payment provider status
	 * @request GET:/api/v1/payments/status
	 * @secure
	 */
	export namespace PaymentProviderStatus {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
}

export namespace Misc {
	/**
	 * No description
	 * @tags Misc
	 * @name GetGiifTypes
	 * @summary to get fetch giif types(source of income options)
	 * @request GET:/api/v1/misc/giif-types
	 */
	export namespace GetGiifTypes {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Misc
	 * @name CheckVersion
	 * @summary provides information on a current allowed version of an app
	 * @request POST:/api/v1/misc/versions
	 */
	export namespace CheckVersion {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = AppVersionDto;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = AppVersionResponseDto;
	}
	/**
	 * No description
	 * @tags Misc
	 * @name RefreshConfig
	 * @summary force refresh middleware's config from the database.
	 * @request GET:/api/v1/misc/refresh-config/{pass}
	 */
	export namespace RefreshConfig {
		export type RequestParams = {
			/**
			 * Password is required to prevent being called from internet, it is a timestamp that is not older than 5sec from now.
			 * @example "12567417"
			 */
			pass: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = void;
	}
}

export namespace Integrations {
	/**
	 * No description
	 * @tags Integrations
	 * @name GetTvbet
	 * @summary to get details of tvbet integration, use integration for card-games
	 * @request GET:/api/v1/integrations/tvbet
	 * @secure
	 */
	export namespace GetTvbet {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: TvbetResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetStatscoreEventsBulk
	 * @summary to get statscore event id by prematch event id
	 * @request GET:/api/v1/integrations/statscore
	 */
	export namespace GetStatscoreEventsBulk {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: StatscoreResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetStatscoreEvent
	 * @summary to get statscore event id by prematch event id
	 * @request GET:/api/v1/integrations/statscore/{eventId}
	 */
	export namespace GetStatscoreEvent {
		export type RequestParams = {
			/** event id of prematch event */
			eventId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: StatscoreEventResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetBetradarLiveStream
	 * @summary to get betradar live stream url
	 * @request GET:/api/v1/integrations/betradar-live-stream/{streamId}/{streamType}/{device}
	 * @secure
	 */
	export namespace GetBetradarLiveStream {
		export type RequestParams = {
			/** stream id of event */
			streamId: string;
			/** stream type */
			streamType: 'hls-manifest' | 'dash-manifest';
			/** device type */
			device: 'Mobile' | 'Desktop';
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: BetradarLiveStreamResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetSisLiveStreamBySport
	 * @summary To get sis live stream url
	 * @request GET:/api/v1/integrations/sis-live-stream/{sportId}
	 * @secure
	 */
	export namespace GetSisLiveStreamBySport {
		export type RequestParams = {
			sportId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SisLiveStreamResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetSisLiveStreamByEventOrSport
	 * @summary To get sis live stream url by eventId or sportId
	 * @request GET:/api/v1/integrations/sis-live-stream/event-id/{eventId}/sport-id/{sportId}
	 * @secure
	 */
	export namespace GetSisLiveStreamByEventOrSport {
		export type RequestParams = {
			eventId: string;
			sportId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SisLiveStreamResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetLow6Token
	 * @request GET:/api/v1/integrations/low6
	 */
	export namespace GetLow6Token {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetLow6TokenForLoggedUser
	 * @request GET:/api/v1/integrations/low6logged
	 * @secure
	 */
	export namespace GetLow6TokenForLoggedUser {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name ValidateLoggedLow6Token
	 * @request GET:/api/v1/integrations/low6logged/validate
	 */
	export namespace ValidateLoggedLow6Token {
		export type RequestParams = {};
		export type RequestQuery = {
			token: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name ValidateToken
	 * @request GET:/api/v1/integrations/low6/validate
	 */
	export namespace ValidateToken {
		export type RequestParams = {};
		export type RequestQuery = {
			token: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * @description Retrieve all prematch events for today and tomorrow. URLs are added to the events.
	 * @tags Integrations
	 * @name GetTodaysEvents
	 * @summary Get Prematch Events for 2 days
	 * @request GET:/api/v1/integrations/sportbetfan/prematch
	 */
	export namespace GetTodaysEvents {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto[];
	}
	/**
	 * @description Retrieve all prematch events for today and tomorrow. URLs are added to the events.
	 * @tags Integrations
	 * @name GetTodaysEvents2
	 * @summary Get Prematch Events for 2 days
	 * @request GET:/api/v1/integrations/marketing/prematch
	 * @originalName getTodaysEvents
	 * @duplicate
	 */
	export namespace GetTodaysEvents2 {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto[];
	}
	/**
	 * @description Retrieve specific prematch events by their IDs. URLs are added to the events.
	 * @tags Integrations
	 * @name GetEvent
	 * @summary Get Specific Prematch Events
	 * @request GET:/api/v1/integrations/sportbetfan/prematch/{eventIds}
	 */
	export namespace GetEvent {
		export type RequestParams = {
			/**
			 * Comma-separated list of event IDs to fetch details for.
			 * @example "123,456,789"
			 */
			eventIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto;
	}
	/**
	 * @description Retrieve specific prematch events by their IDs. URLs are added to the events.
	 * @tags Integrations
	 * @name GetEvent2
	 * @summary Get Specific Prematch Events
	 * @request GET:/api/v1/integrations/marketing/prematch/{eventIds}
	 * @originalName getEvent
	 * @duplicate
	 */
	export namespace GetEvent2 {
		export type RequestParams = {
			/**
			 * Comma-separated list of event IDs to fetch details for.
			 * @example "123,456,789"
			 */
			eventIds: string[];
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto;
	}
	/**
	 * @description Retrieve all live events currently happening. URLs are added to the events.
	 * @tags Integrations
	 * @name GetLiveEvents
	 * @summary Get Live Events
	 * @request GET:/api/v1/integrations/sportbetfan/live
	 */
	export namespace GetLiveEvents {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto[];
	}
	/**
	 * @description Retrieve all live events currently happening. URLs are added to the events.
	 * @tags Integrations
	 * @name GetLiveEvents2
	 * @summary Get Live Events
	 * @request GET:/api/v1/integrations/marketing/live
	 * @originalName getLiveEvents
	 * @duplicate
	 */
	export namespace GetLiveEvents2 {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventDto[];
	}
	/**
	 * @description Retrieve all available event categories.
	 * @tags Integrations
	 * @name GetCategories
	 * @summary Get Event Categories
	 * @request GET:/api/v1/integrations/sportbetfan/categories
	 */
	export namespace GetCategories {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = EventCategoryDto[];
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetBestsellers
	 * @request GET:/api/v1/integrations/sportbetfan/bestsellers
	 */
	export namespace GetBestsellers {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @tags Integrations
	 * @name GetPlayersCoupon
	 * @request GET:/api/v1/integrations/sportbetfan/kupon-kibica/{id}
	 */
	export namespace GetPlayersCoupon {
		export type RequestParams = {
			id: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
}

export namespace Virtuals {
	/**
	 * No description
	 * @tags Virtuals
	 * @name GetRunningSports
	 * @summary to get list of running sports
	 * @request GET:/api/v1/virtuals/running-sports
	 */
	export namespace GetRunningSports {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: VirtualRunningSportsResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Virtuals
	 * @name GenerateEventOutcomes
	 * @summary to generated outcome of virtual sport
	 * @request POST:/api/v1/virtuals/generate-outcomes
	 */
	export namespace GenerateEventOutcomes {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = GenerateUofOutcomesBodyDto;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: GenerateUofOutcomesResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
}

export namespace KuponKibica {
	/**
	 * No description
	 * @tags Kupon Kibica
	 * @name FindOne
	 * @request GET:/api/v1/kupon-kibica/{id}
	 */
	export namespace FindOne {
		export type RequestParams = {
			id: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody =
			| {
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: KuponKibicaResponseDto;
			  }
			| {
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
			  };
	}
	/**
	 * No description
	 * @tags Kupon Kibica
	 * @name FindAndShare
	 * @request GET:/api/v1/kupon-kibica/{id}/share/{name}
	 */
	export namespace FindAndShare {
		export type RequestParams = {
			id: string;
			name: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = {
			/** @example true */
			isError: true;
			/** @example 500 */
			code: number;
			message: string;
		};
	}
}

export namespace PlayerProfiles {
	/**
	 * @description Fetch the available avatars and their categories for the user based on their customerId.
	 * @tags Player's Profile
	 * @name GetAvailableAvatars
	 * @summary Get available avatars and categories for the user.
	 * @request GET:/api/v1/player-profiles/avatars
	 * @secure
	 */
	export namespace GetAvailableAvatars {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = AvatarsResponse;
	}
	/**
	 * @description Save the selected avatar (identified by avatarId) to the user's profile.
	 * @tags Player's Profile
	 * @name SavePlayerAvatar
	 * @summary Save player avatar to the profile.
	 * @request PUT:/api/v1/player-profiles/avatars/{avatarId}
	 * @secure
	 */
	export namespace SavePlayerAvatar {
		export type RequestParams = {
			/** The ID of the avatar to be saved. */
			avatarId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = SaveProfileResponseDto;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name GetAvailableMedals
	 * @request GET:/api/v1/player-profiles/medals
	 * @secure
	 */
	export namespace GetAvailableMedals {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name ClaimMedal
	 * @request PUT:/api/v1/player-profiles/medals/{medalId}
	 * @secure
	 */
	export namespace ClaimMedal {
		export type RequestParams = {
			medalId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name SavePlayersProfile
	 * @request PUT:/api/v1/player-profiles
	 * @secure
	 */
	export namespace SavePlayersProfile {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = PutPlayerProfileDto;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name GetObserved
	 * @request GET:/api/v1/player-profiles/observed
	 * @secure
	 */
	export namespace GetObserved {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name AddToObserved
	 * @request POST:/api/v1/player-profiles/observed
	 * @secure
	 */
	export namespace AddToObserved {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ObservedDto;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
	/**
	 * No description
	 * @tags Player's Profile
	 * @name RemoveFromObserved
	 * @request DELETE:/api/v1/player-profiles/observed
	 * @secure
	 */
	export namespace RemoveFromObserved {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = ObservedDto;
		export type RequestHeaders = {};
		export type ResponseBody = any;
	}
}

export namespace DailyChallenge {
	/**
	 * @description Retrieve all active daily challenges for the current day.
	 * @tags Daily Challenge
	 * @name GetTodaysChallenges
	 * @summary Get Today's Challenges
	 * @request GET:/api/v1/daily-challenge
	 */
	export namespace GetTodaysChallenges {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = DailyChallengeModel[];
	}
}

export namespace Analytics {
	/**
	 * No description
	 * @tags Analytics
	 * @name CollectData
	 * @request POST:/api/v1/analytics/betslips
	 */
	export namespace CollectData {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = AnalyticsObject;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
}

export namespace Authologic {
	/**
	 * No description
	 * @name InitializeAuthologic
	 * @request POST:/api/v1/authologic/initialize/{conversationType}
	 */
	export namespace InitializeAuthologic {
		export type RequestParams = {
			conversationType: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name AcceptCallback
	 * @request POST:/api/v1/authologic/callback/{conversationType}/{authologicConversationId}
	 */
	export namespace AcceptCallback {
		export type RequestParams = {
			conversationType: string;
			authologicConversationId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name CheckConversationResult
	 * @request GET:/api/v1/authologic/check/{conversationType}/{authologicConversationId}
	 */
	export namespace CheckConversationResult {
		export type RequestParams = {
			conversationType: string;
			authologicConversationId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name FinishRegistration
	 * @request POST:/api/v1/authologic/finish/registrationMojeId/{authologicConversationId}
	 */
	export namespace FinishRegistration {
		export type RequestParams = {
			authologicConversationId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = FinishRegistrationDto;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name FinishVerification
	 * @request POST:/api/v1/authologic/finish/verificationMojeId/{authologicConversationId}
	 */
	export namespace FinishVerification {
		export type RequestParams = {
			authologicConversationId: string;
		};
		export type RequestQuery = {};
		export type RequestBody = FinishVerificationDto;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
}

export namespace EventFilters {
	/**
	 * No description
	 * @tags EventFilters
	 * @name GetAvailableFiltersForCategory
	 * @summary Returns available event filters by category ID
	 * @request GET:/api/v1/event-filters/category/{categoryId}
	 */
	export namespace GetAvailableFiltersForCategory {
		export type RequestParams = {
			categoryId: number;
		};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = AvailableFiltersResponse;
	}
	/**
	 * No description
	 * @tags EventFilters
	 * @name GetAvailableFiltersForEsport
	 * @summary Returns available event filters by e-sports
	 * @request GET:/api/v1/event-filters/esport
	 */
	export namespace GetAvailableFiltersForEsport {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {
			/**
			 * Language in which response needed. If incorrect language code or no code provided then default language code PL will be use
			 * @default "pl"
			 */
			'request-language': 'pl' | 'en' | 'uk';
		};
		export type ResponseBody = AvailableFiltersResponse;
	}
}

export namespace LoadTest {
	/**
	 * No description
	 * @name CpuIntensive
	 * @request GET:/api/v1/load-test/cpu-intensive
	 */
	export namespace CpuIntensive {
		export type RequestParams = {};
		export type RequestQuery = {
			duration: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name MemoryIntensive
	 * @request GET:/api/v1/load-test/memory-intensive
	 */
	export namespace MemoryIntensive {
		export type RequestParams = {};
		export type RequestQuery = {
			size: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name MixedLoad
	 * @request POST:/api/v1/load-test/mixed-load
	 */
	export namespace MixedLoad {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name Fibonacci
	 * @request GET:/api/v1/load-test/fibonacci
	 */
	export namespace Fibonacci {
		export type RequestParams = {};
		export type RequestQuery = {
			n: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name HashIntensive
	 * @request GET:/api/v1/load-test/hash-intensive
	 */
	export namespace HashIntensive {
		export type RequestParams = {};
		export type RequestQuery = {
			iterations: string;
		};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
	/**
	 * No description
	 * @name GetHealth
	 * @request GET:/api/v1/load-test/health
	 */
	export namespace GetHealth {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
}

export namespace Ping {
	/**
	 * No description
	 * @name PingV1
	 * @request GET:/api/v1/ping
	 */
	export namespace PingV1 {
		export type RequestParams = {};
		export type RequestQuery = {};
		export type RequestBody = never;
		export type RequestHeaders = {};
		export type ResponseBody = void;
	}
}

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, HeadersDefaults, ResponseType } from 'axios';

export type QueryParamsType = Record<string | number, any>;

export interface FullRequestParams extends Omit<AxiosRequestConfig, 'data' | 'params' | 'url' | 'responseType'> {
	/** set parameter to `true` for call `securityWorker` for this request */
	secure?: boolean;
	/** request path */
	path: string;
	/** content type of request body */
	type?: ContentType;
	/** query params */
	query?: QueryParamsType;
	/** format of response (i.e. response.json() -> format: "json") */
	format?: ResponseType;
	/** request body */
	body?: unknown;
}

export type RequestParams = Omit<FullRequestParams, 'body' | 'method' | 'query' | 'path'>;

export interface ApiConfig<SecurityDataType = unknown> extends Omit<AxiosRequestConfig, 'data' | 'cancelToken'> {
	securityWorker?: (securityData: SecurityDataType | null) => Promise<AxiosRequestConfig | void> | AxiosRequestConfig | void;
	secure?: boolean;
	format?: ResponseType;
}

export enum ContentType {
	Json = 'application/json',
	FormData = 'multipart/form-data',
	UrlEncoded = 'application/x-www-form-urlencoded',
	Text = 'text/plain',
}

export class HttpClient<SecurityDataType = unknown> {
	public instance: AxiosInstance;
	private securityData: SecurityDataType | null = null;
	private securityWorker?: ApiConfig<SecurityDataType>['securityWorker'];
	private secure?: boolean;
	private format?: ResponseType;

	constructor({ securityWorker, secure, format, ...axiosConfig }: ApiConfig<SecurityDataType> = {}) {
		this.instance = axios.create({ ...axiosConfig, baseURL: axiosConfig.baseURL || '' });
		this.secure = secure;
		this.format = format;
		this.securityWorker = securityWorker;
	}

	public setSecurityData = (data: SecurityDataType | null) => {
		this.securityData = data;
	};

	protected mergeRequestParams(params1: AxiosRequestConfig, params2?: AxiosRequestConfig): AxiosRequestConfig {
		const method = params1.method || (params2 && params2.method);

		return {
			...this.instance.defaults,
			...params1,
			...(params2 || {}),
			headers: {
				...((method && this.instance.defaults.headers[method.toLowerCase() as keyof HeadersDefaults]) || {}),
				...(params1.headers || {}),
				...((params2 && params2.headers) || {}),
			},
		};
	}

	protected stringifyFormItem(formItem: unknown) {
		if (typeof formItem === 'object' && formItem !== null) {
			return JSON.stringify(formItem);
		} else {
			return `${formItem}`;
		}
	}

	protected createFormData(input: Record<string, unknown>): FormData {
		return Object.keys(input || {}).reduce((formData, key) => {
			const property = input[key];
			const propertyContent: any[] = property instanceof Array ? property : [property];

			for (const formItem of propertyContent) {
				const isFileType = formItem instanceof Blob || formItem instanceof File;
				formData.append(key, isFileType ? formItem : this.stringifyFormItem(formItem));
			}

			return formData;
		}, new FormData());
	}

	public request = async <T = any, _E = any>({
		secure,
		path,
		type,
		query,
		format,
		body,
		...params
	}: FullRequestParams): Promise<AxiosResponse<T>> => {
		const secureParams =
			((typeof secure === 'boolean' ? secure : this.secure) && this.securityWorker && (await this.securityWorker(this.securityData))) || {};
		const requestParams = this.mergeRequestParams(params, secureParams);
		const responseFormat = format || this.format || undefined;

		if (type === ContentType.FormData && body && body !== null && typeof body === 'object') {
			body = this.createFormData(body as Record<string, unknown>);
		}

		if (type === ContentType.Text && body && body !== null && typeof body !== 'string') {
			body = JSON.stringify(body);
		}

		return this.instance.request({
			...requestParams,
			headers: {
				...(requestParams.headers || {}),
				...(type && type !== ContentType.FormData ? { 'Content-Type': type } : {}),
			},
			params: query,
			responseType: responseFormat,
			data: body,
			url: path,
		});
	};
}

/**
 * @title Betfan Backend Middleware
 * @version 1.0
 * @contact
 *
 * Middleware and backend software, modifies request/response acts as a separation layer between Betfan's frontend and core system/feed providers.
 */
export class Api<SecurityDataType extends unknown> extends HttpClient<SecurityDataType> {
	/**
	 * No description
	 *
	 * @name Ping
	 * @request GET:/api/ping
	 */
	ping = (params: RequestParams = {}) =>
		this.request<void, any>({
			path: `/api/ping`,
			method: 'GET',
			...params,
		});

	Livebetting = {
		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningGames
		 * @summary to get live running games
		 * @request GET:/api/v1/livebetting/running/games/{gameClass}
		 */
		getRunningGames: (
			gameClass: 'main' | 'major' | 'all',
			query: {
				/** whether game types codes needed or not */
				getGameTypeCodes: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningGamesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/games/${gameClass}`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRefreshOdds
		 * @summary to get latest odds of outcomes
		 * @request GET:/api/v1/livebetting/running/refresh-odds
		 */
		getRefreshOdds: (
			query: {
				/** outcome ids to fetch odds */
				outcomeIds: string[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RefreshOddsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/refresh-odds`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEvents
		 * @summary to live events based on event id
		 * @request GET:/api/v1/livebetting/running/events
		 */
		getRunningEvents: (
			query: {
				/** whether game types codes needed or not */
				getGameTypeCodes: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEventsBySport
		 * @summary to get live running events of sports
		 * @request GET:/api/v1/livebetting/running/events/sports/{sportIds}
		 */
		getRunningEventsBySport: (sportIds: string[], params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEventsBySportResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/sports/${sportIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEventsEsports
		 * @summary to get live running esports events
		 * @request GET:/api/v1/livebetting/running/events/esports/game-class/{gameClass}
		 */
		getRunningEventsEsports: (gameClass: 'main' | 'major' | 'all', params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEsportEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/esports/game-class/${gameClass}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description to fetch esport event details
		 *
		 * @tags Livebetting
		 * @name GetRunningEsportEvent
		 * @request GET:/api/v1/livebetting/running/events/esports/{eventIds}
		 */
		getRunningEsportEvent: (eventIds: string[], params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEsportEventsByIdResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/esports/${eventIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEsportEvents
		 * @summary to get live running esports events
		 * @request GET:/api/v1/livebetting/running/events/esports
		 */
		getRunningEsportEvents: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEsportEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/esports`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetAllRunningEsportEvents
		 * @summary to get All running esports events
		 * @request GET:/api/v1/livebetting/running/events/all
		 */
		getAllRunningEsportEvents: (
			query?: {
				/** search based on given number of hours or days from now */
				filter?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: GroupedRunningEsportEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/all`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEventsByIds
		 * @summary to live events based on event id
		 * @request GET:/api/v1/livebetting/running/events/{eventIds}
		 */
		getRunningEventsByIds: (eventIds: string[], params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEventsByIdResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/events/${eventIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningSportsCategories
		 * @summary to get live running sports categories
		 * @request GET:/api/v1/livebetting/running/categories/sports
		 */
		getRunningSportsCategories: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningSportCategoriesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/categories/sports`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetRunningEventsByCategoriesForDropdown
		 * @summary to get live running events by categories
		 * @request GET:/api/v1/livebetting/running/levels/{level}/categories/{ids}/events/dropdown
		 */
		getRunningEventsByCategoriesForDropdown: (ids: string[], level: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEventsDropdownResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/levels/${level}/categories/${ids}/events/dropdown`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetEventsCount
		 * @summary to get live running events count
		 * @request GET:/api/v1/livebetting/running/event-count
		 */
		getEventsCount: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RunningEventCountResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/livebetting/running/event-count`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Livebetting
		 * @name GetPopularLiveEventsByIds
		 * @request GET:/api/v1/livebetting/running/popular
		 */
		getPopularLiveEventsByIds: (
			query: {
				/** categoryIds */
				categoryIds: string[];
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/v1/livebetting/running/popular`,
				method: 'GET',
				query: query,
				...params,
			}),
	};
	Market = {
		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetGameOutcomesData
		 * @summary list outcome id list
		 * @request GET:/api/v1/market/game-outcomes-data/{outcomeIds}
		 */
		getGameOutcomesData: (outcomeIds: string[], params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: GetGameOutcomeResponseDTO;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/game-outcomes-data/${outcomeIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetBestsellerGames
		 * @summary to get best sellers events
		 * @request GET:/api/v1/market/events/bestsellers
		 */
		getBestsellerGames: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BestsellerEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/bestsellers`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEsportsEvents
		 * @summary to get best sellers esports events
		 * @request GET:/api/v1/market/events/esports
		 */
		getEsportsEvents: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BestsellerEsportEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/esports`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventsByGamesIds
		 * @summary to get events by games ids
		 * @request GET:/api/v1/market/events/games/{gameIds}
		 */
		getEventsByGamesIds: (gameIds: string[], params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventsByGameIdsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/games/${gameIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventsByQuickSearch
		 * @summary to search event based on phrase
		 * @request POST:/api/v1/market/events/quick-search
		 */
		getEventsByQuickSearch: (data: EventQuickSearchBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventQuickSearchResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/quick-search`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventsStatscore
		 * @summary to get mapping of event id to statscore id
		 * @request GET:/api/v1/market/events/statscore
		 */
		getEventsStatscore: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventsStatscoreResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/statscore`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetTodaysEvents
		 * @summary to get today events list
		 * @request GET:/api/v1/market/events/today-offers/{todayDate}
		 */
		getTodaysEvents: (todayDate: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: TodayEventsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/today-offers/${todayDate}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventDetails
		 * @summary to fetch event details which is not started yet
		 * @request GET:/api/v1/market/events/{eventId}
		 */
		getEventDetails: (
			eventId: string,
			query: {
				/** sport Id to filter SBB query */
				sportId: number;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventByIdResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/events/${eventId}`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetCategories
		 * @summary to get categories, used in discipline sports
		 * @request GET:/api/v1/market/categories
		 */
		getCategories: (
			query?: {
				/** search based on given number of hours or days from now */
				filter?: string;
				/** list categories only found in this sports */
				sportsId?: string;
				/** list categories only found in this league/parent category */
				parentId?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CategoriesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetAllCategories
		 * @summary to get all categories
		 * @request GET:/api/v1/market/categories/all
		 */
		getAllCategories: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AllCategoriesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories/all`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetCategoriesDescriptions
		 * @summary to get categories descriptions
		 * @request GET:/api/v1/market/categories/descriptions
		 */
		getCategoriesDescriptions: (
			query: {
				/**
				 * list of categories ids to find their descriptions
				 * @format csv
				 */
				categoryIds: string[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CategoriesDescriptionsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories/descriptions`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetCategoriesFlag
		 * @summary to get categories flag
		 * @request GET:/api/v1/market/categories/flags
		 */
		getCategoriesFlag: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CategoriesFlagsResponse;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories/flags`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventsByCategories
		 * @summary to fetch events by categories
		 * @request GET:/api/v1/market/categories/{categoryId}/events
		 */
		getEventsByCategories: (
			categoryId: number,
			query?: {
				/** search based on given number of hours or days from now */
				filter?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventsByCategoriesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories/${categoryId}/events`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Market
		 * @name GetEventsByCategoryForDropdown
		 * @summary to fetch event of category for display them in dropdown breadcomb
		 * @request GET:/api/v1/market/categories/{categoryId}/events/dropdown
		 */
		getEventsByCategoryForDropdown: (categoryId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EventsByCategoryForDropdownResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/market/categories/${categoryId}/events/dropdown`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	Customer = {
		/**
		 * No description
		 *
		 * @tags Customer
		 * @name Login
		 * @summary to login
		 * @request POST:/api/v1/customer/session/login
		 * @secure
		 */
		login: (data: LoginDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: LoginResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/session/login`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name Logout
		 * @summary to logout
		 * @request DELETE:/api/v1/customer/session/logout
		 * @secure
		 */
		logout: (
			query?: {
				'device-code'?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: LogoutResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/session/logout`,
				method: 'DELETE',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name KeepAliveSession
		 * @summary to check whether user session alive or not
		 * @request GET:/api/v1/customer/session/keep-alive
		 * @secure
		 */
		keepAliveSession: (
			query: {
				/** whether to keep-alive session or not */
				keepAlive: boolean;
				/**
				 * whether to need last stake amount of customer or not
				 * @default false
				 */
				needLastStake?: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: KeepAliveResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/session/keep-alive`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetSessionHistory
		 * @summary to get users sessions history
		 * @request GET:/api/v1/customer/session/history
		 * @secure
		 */
		getSessionHistory: (
			query: {
				/** start date for seach in dd-mm-yyyy format */
				dateFrom?: string;
				/** end date for seach in dd-mm-yyyy format */
				dateTo?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * no of record to fetch
				 * @default 0
				 */
				offset: number;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SessionHistoryResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/session/history`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetAccountLimit
		 * @summary to users account limit
		 * @request GET:/api/v1/customer/account/limit
		 * @secure
		 */
		getAccountLimit: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AccountLimitsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/limit`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name UpdateAccountLimit
		 * @summary to update customer account limits
		 * @request PUT:/api/v1/customer/account/limit
		 * @secure
		 */
		updateAccountLimit: (data: UpdateLimitBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AccountLimitsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/limit`,
				method: 'PUT',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetVip
		 * @summary To check if the customer is VIP
		 * @request GET:/api/v1/customer/segmentation/vip/level
		 * @secure
		 */
		getVip: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: number | any[];
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/segmentation/vip/level`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetVerificationStatus
		 * @summary to verification status of user account and customer data
		 * @request GET:/api/v1/customer/account/verification-status
		 * @secure
		 */
		getVerificationStatus: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: VerificationStatusResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/verification-status`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name ChangePassword
		 * @summary to change password of user
		 * @request POST:/api/v1/customer/account/change-password
		 * @secure
		 */
		changePassword: (data: ChangePasswordDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ChangePasswordResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/change-password`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name PreRegister
		 * @summary to send SMS code to user mobile, pre-registration step
		 * @request POST:/api/v1/customer/account/pre-register
		 * @secure
		 */
		preRegister: (data: PreRegisterBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PreRegisterResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/pre-register`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name Register
		 * @summary to complete registration
		 * @request POST:/api/v1/customer/account/register
		 * @secure
		 */
		register: (data: RegisterBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: RegisterResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/register`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name CheckFieldValueUnique
		 * @summary to check given field and value is unique or not
		 * @request GET:/api/v1/customer/account/is-value-unique/{fieldName}/{fieldValue}
		 * @secure
		 */
		checkFieldValueUnique: (
			fieldName: 'email' | 'personalIdentifier' | 'mobile' | 'document-number',
			fieldValue: string,
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: boolean;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/is-value-unique/${fieldName}/${fieldValue}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name ActivateTemporaryAccount
		 * @summary to activate user account after verification
		 * @request POST:/api/v1/customer/account/activate-temporary-account
		 * @secure
		 */
		activateTemporaryAccount: (data: ActivateAccountBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: boolean;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/activate-temporary-account`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name ResetPassword
		 * @summary to send reset password link
		 * @request POST:/api/v1/customer/account/reset-password
		 * @secure
		 */
		resetPassword: (data: ResetPasswordDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: boolean;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/reset-password`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name ResetPasswordVerify
		 * @summary to verify reset password
		 * @request POST:/api/v1/customer/account/reset-password-verify
		 * @secure
		 */
		resetPasswordVerify: (data: ResetPasswordVerifyBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: boolean;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/reset-password-verify`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name CloseAccount
		 * @summary to close account
		 * @request POST:/api/v1/customer/account/close
		 * @secure
		 */
		closeAccount: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CloseAccountResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/close`,
				method: 'POST',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name SelfExclusionAccount
		 * @summary to do self exclusion, use 1 day in body to suspend account
		 * @request POST:/api/v1/customer/account/self-exclusion
		 * @secure
		 */
		selfExclusionAccount: (data: UpdateSelfExclusionBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AccountLimitsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/self-exclusion`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetAccountInformation
		 * @summary to get account information
		 * @request GET:/api/v1/customer/account/information
		 * @secure
		 */
		getAccountInformation: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AccountInformationResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/information`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name FinishVerification
		 * @summary to request for verification to admin
		 * @request POST:/api/v1/customer/account/verification-finish
		 * @secure
		 */
		finishVerification: (data: FinishVerificationBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: FinishVerificationResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/verification-finish`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.FormData,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetApprovedSlips
		 * @summary To get approved slips data
		 * @request GET:/api/v1/customer/session/approved-slips
		 * @secure
		 */
		getApprovedSlips: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ApprovedSlipsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/session/approved-slips`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetConsentTags
		 * @request GET:/api/v1/customer/account/consents
		 * @secure
		 */
		getConsentTags: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ConsentTagResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/consents`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name ChangeAccountConsent
		 * @request POST:/api/v1/customer/account/consents
		 * @secure
		 */
		changeAccountConsent: (data: ChangeConsentRequestDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ChangeConsentResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/customer/account/consents`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Customer
		 * @name GetPersonalData
		 * @request GET:/api/v1/customer/personal-data
		 * @secure
		 */
		getPersonalData: (params: RequestParams = {}) =>
			this.request<PersonalDataResponseDto, void>({
				path: `/api/v1/customer/personal-data`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),
	};
	Betting = {
		/**
		 * No description
		 *
		 * @tags Betting
		 * @name CalculateTaxAndWinning
		 * @summary to calculate tax winning
		 * @request POST:/api/v1/betting/calculate-tax-winning/{registerMethod}
		 * @secure
		 */
		calculateTaxAndWinning: (registerMethod: string, data: CalculateTaxWinningDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CalculateTaxAndWinningResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				NotFoundErrorDto | void
			>({
				path: `/api/v1/betting/calculate-tax-winning/${registerMethod}`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name PlaceBet
		 * @summary to place bet
		 * @request POST:/api/v1/betting/place-bet/{registerMethod}
		 * @secure
		 */
		placeBet: (registerMethod: string, data: CalculateTaxWinningDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PlaceBetResponseDto;
				  }
				| BetActionErrorResponseDto
				| PlaceBetResponseDto
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/place-bet/${registerMethod}`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name ApproveBet
		 * @summary To be called by front-end if place-bet api gives error code 30
		 * @request POST:/api/v1/betting/approve-bet/{registerMethod}
		 * @secure
		 */
		approveBet: (registerMethod: string, data: CalculateTaxWinningDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ApproveBetResponseDto;
				  }
				| BetActionErrorResponseDto
				| ApproveBetResponseDto
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/approve-bet/${registerMethod}`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name FindBestOutcome
		 * @summary to find best outcome based on given filters
		 * @request GET:/api/v1/betting/outcomes
		 * @secure
		 */
		findBestOutcome: (
			query: {
				/** minimum odds */
				fromOdds: string;
				/** maximum odds */
				toOdds: string;
				/** stake to put */
				stake: string;
				/** possible win */
				amount: string;
				/** search in the today events */
				today: boolean;
				/** search in the tomorrow events */
				tomorrow: boolean;
				/** search in the day after events */
				dayAfter: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: FindBestOutcomesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/outcomes`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name ValidateCustomBets
		 * @summary to validate outcome for custom bet and find odds
		 * @request GET:/api/v1/betting/custom-bet/validate
		 */
		validateCustomBets: (
			query: {
				/** outcome ids for validate and find odds */
				outcomeIds: string[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ValidateCustomBetsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/custom-bet/validate`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name CalculateCustomBets
		 * @summary to calculate/generate outcome for custom bet
		 * @request GET:/api/v1/betting/custom-bet/calculate
		 */
		calculateCustomBets: (
			query: {
				/** outcome ids for calculate and generate new outcome */
				outcomeIds: string[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CalculateCustomBetsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/custom-bet/calculate`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Betting
		 * @name UpdateCustomBetOdds
		 * @summary to update custom bet odds
		 * @request GET:/api/v1/betting/custom-bet/update-odds/{outcomeId}
		 */
		updateCustomBetOdds: (outcomeId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: UpdateCustomBetOddsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/betting/custom-bet/update-odds/${outcomeId}`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	Transaction = {
		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name GetTransactionsByDate
		 * @summary to get transactions by date filters, use for coupons history
		 * @request GET:/api/v1/transaction/by-date
		 * @secure
		 */
		getTransactionsByDate: (
			query: {
				/** start date for seach in yyyy-mm-dd format */
				from?: string;
				/** end date for seach in yyyy-mm-dd format */
				to?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * no of record to fetch
				 * @default 0
				 */
				offset?: number;
				pageToken?: string;
				/** transaction types to filter */
				type: 'maxizysk' | 'won' | 'lost' | 'undecided' | 'all' | 'tvBet' | 'cashout';
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: TransactionByDateResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/transaction/by-date`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name AnnotateTransaction
		 * @summary to give a trasansaction its custom name
		 * @request POST:/api/v1/transaction/annotate
		 * @secure
		 */
		annotateTransaction: (data: AnnotateTransactionDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AnnotateTransactionDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/transaction/annotate`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name GetNumberActiveBetslips
		 * @summary returns number of active betslips
		 * @request GET:/api/v1/transaction/number-active-betslips
		 * @secure
		 */
		getNumberActiveBetslips: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
				},
				void
			>({
				path: `/api/v1/transaction/number-active-betslips`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name GetTransfersByDate
		 * @summary to get transfers by date filters, use to show user transfer activities
		 * @request GET:/api/v1/transaction/transfers/by-date
		 * @secure
		 */
		getTransfersByDate: (
			query: {
				/** start date for seach in yyyy-mm-dd format */
				from?: string;
				/** end date for seach in yyyy-mm-dd format */
				to?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * no of record to fetch
				 * @default 0
				 */
				offset: number;
				/** which types of transfers needed */
				type: 'all' | 'payout' | 'deposit';
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: TransfersByDateResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/transaction/transfers/by-date`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name CustomerHasBetPlacedWithin
		 * @summary to know whether customer has placed bet in given days or not
		 * @request GET:/api/v1/transaction/has-customer-placed-bet
		 * @secure
		 */
		customerHasBetPlacedWithin: (
			query: {
				/** no of days to account */
				days: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CustomerHasPlacedBetResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/transaction/has-customer-placed-bet`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Transaction
		 * @name GetSettledSlipAfterLastLogin
		 * @summary to get settled slip data after last login
		 * @request GET:/api/v1/transaction/slips-settled-after-last-login
		 * @secure
		 */
		getSettledSlipAfterLastLogin: (
			query: {
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * no of record to fetch
				 * @default 0
				 */
				offset: number;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SlipsSettledAfterLastLoginResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/transaction/slips-settled-after-last-login`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),
	};
	Cashout = {
		/**
		 * No description
		 *
		 * @tags Cashout
		 * @name GetMaximumCashout
		 * @summary to view available cashout on given slip
		 * @request GET:/api/v1/cashout/max/{slipId}
		 * @secure
		 */
		getMaximumCashout: (slipId: number, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: AvailableCashoutResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cashout/max/${slipId}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * @description NOTE: For live event call this api after calling prepare-live-countdown api and wait 10 seconds
		 *
		 * @tags Cashout
		 * @name DoCashout
		 * @summary to do cashout
		 * @request POST:/api/v1/cashout/full/{slipId}
		 * @secure
		 */
		doCashout: (slipId: number, data: FullCashoutBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: FullCashoutResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cashout/full/${slipId}`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * @description This api need to be call before live event cashout.
		 *
		 * @tags Cashout
		 * @name PrepareCashout
		 * @summary to be called by app before doing full cashout for live event
		 * @request POST:/api/v1/cashout/prepare-live-countdown/{slipId}
		 * @secure
		 */
		prepareCashout: (slipId: number, data: PrepareCashoutBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PrepareLiveCountdownResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cashout/prepare-live-countdown/${slipId}`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),
	};
	Cms = {
		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetHotBanners
		 * @summary to get banners based on query parameters
		 * @request GET:/api/v1/cms/hot
		 */
		getHotBanners: (
			query?: {
				/** to get banners for logged users */
				forNonLogged?: boolean;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BannersResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/hot`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetSubBanners
		 * @summary to get sub-banners
		 * @request GET:/api/v1/cms/sub-banners
		 */
		getSubBanners: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SubBannersResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/sub-banners`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetRegistrationBanners
		 * @summary to get registration banner list
		 * @request GET:/api/v1/cms/registration/banners
		 */
		getRegistrationBanners: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: RegistrationBannersResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/registration/banners`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetEntertainments
		 * @summary to get entertainments based on categories and paginations
		 * @request GET:/api/v1/cms/entertainments
		 */
		getEntertainments: (
			query?: {
				/**
				 * no of records to fetch
				 * @default 20
				 */
				limit?: string;
				/**
				 * from which page to fetch
				 * @default 1
				 */
				pageNo?: string;
				/** category in which entertainment exist */
				category?: 'politics' | 'current' | 'sports';
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: EntertainmentsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/entertainments`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetPlacements
		 * @summary to get placements based on components
		 * @request GET:/api/v1/cms/placements
		 */
		getPlacements: (
			query: {
				/** screens in which placements exist */
				screen: string;
			},
			params: RequestParams = {},
		) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PlacementsResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/placements`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetRegulations
		 * @summary to get regulations list to use in footer
		 * @request GET:/api/v1/cms/regulations
		 */
		getRegulations: (
			query: {
				/** which types of regulation needed */
				types: ('responsibleGaming' | 'promotions' | 'privacy' | 'privacyPolicy' | 'gdpr' | 'vip')[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: any;
				},
				void
			>({
				path: `/api/v1/cms/regulations`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetPromotions
		 * @summary to get promotions list
		 * @request GET:/api/v1/cms/promotions
		 */
		getPromotions: (
			query?: {
				/**
				 * no of records to fetch
				 * @default 20
				 */
				limit?: string;
				/**
				 * from which page to fetch
				 * @default 1
				 */
				pageNo?: string;
				/** types of promotion to fetch */
				type?: 'constant' | 'temporary';
			},
			params: RequestParams = {},
		) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PromotionsResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/promotions`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetPromotionById
		 * @summary to get promotion details
		 * @request GET:/api/v1/cms/promotions/{promotionSlug}
		 */
		getPromotionById: (promotionSlug: string, params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: PromotionResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/promotions/${promotionSlug}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetRegistrationPromotions
		 * @summary to get registration promotions list
		 * @request GET:/api/v1/cms/registration-promotions
		 */
		getRegistrationPromotions: (
			query: {
				code: string;
			},
			params: RequestParams = {},
		) =>
			this.request<RegistrationPromotionsResponseDto, void>({
				path: `/api/v1/cms/registration-promotions`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name PromotionSignup
		 * @request GET:/api/v1/cms/promotions/signup/{id}
		 * @secure
		 */
		promotionSignup: (id: string, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/promotions/signup/${id}`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetMessages
		 * @summary to get messages list
		 * @request GET:/api/v1/cms/messages
		 * @secure
		 */
		getMessages: (
			query?: {
				/**
				 * no of records to fetch
				 * @default 20
				 */
				limit?: string;
				/**
				 * from which page to fetch
				 * @default 1
				 */
				pageNo?: string;
				/**
				 * whether need to fetch current/active messages
				 * @default true
				 */
				current?: boolean;
				/**
				 * current user id
				 * @deprecated
				 */
				userId?: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages`,
				method: 'GET',
				query: query,
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name DeleteAllMessages
		 * @summary delete all messages
		 * @request DELETE:/api/v1/cms/messages
		 * @secure
		 */
		deleteAllMessages: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages`,
				method: 'DELETE',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetUnreadMessagesCount
		 * @summary to get unread messages count
		 * @request GET:/api/v1/cms/messages/unread
		 * @secure
		 */
		getUnreadMessagesCount: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages/unread`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetMessageById
		 * @summary to get message details
		 * @request GET:/api/v1/cms/messages/{messageId}
		 * @secure
		 */
		getMessageById: (messageId: number, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages/${messageId}`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name MarkMessageRead
		 * @summary to get message details
		 * @request PUT:/api/v1/cms/messages/{messageId}/read
		 * @secure
		 */
		markMessageRead: (messageId: number, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages/${messageId}/read`,
				method: 'PUT',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name DeleteMessages
		 * @request DELETE:/api/v1/cms/messages/{messageIds}
		 * @secure
		 */
		deleteMessages: (messageIds: string[], params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/cms/messages/${messageIds}`,
				method: 'DELETE',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetAppTranslations
		 * @summary to get app translation data
		 * @request GET:/api/v1/cms/app-translation
		 */
		getAppTranslations: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: AppTranslationsResponseDto[];
				},
				void
			>({
				path: `/api/v1/cms/app-translation`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name MyBet
		 * @summary to request custom bet to bookmakers.
		 * @request POST:/api/v1/cms/my-bet
		 * @secure
		 */
		myBet: (data: MybetRequestDto, params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
				},
				void
			>({
				path: `/api/v1/cms/my-bet`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetCustomPage
		 * @summary to get custom page detail
		 * @request GET:/api/v1/cms/custom-page/{slug}
		 */
		getCustomPage: (slug: string, params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: CustomPageResponseDataDto;
				},
				void
			>({
				path: `/api/v1/cms/custom-page/${slug}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name SubmitPromoCode
		 * @summary to submit promo code
		 * @request POST:/api/v1/cms/promo-code
		 */
		submitPromoCode: (data: PromoCodeRequestDto, params: RequestParams = {}) =>
			this.request<CommonMessageResponseDto, void>({
				path: `/api/v1/cms/promo-code`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name SportEventImage
		 * @request GET:/api/v1/cms/sport-event-image
		 */
		sportEventImage: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example false */
					isError: false;
					/** @example 200 */
					code: number;
					data: SportEventImageResponseDto;
				},
				void
			>({
				path: `/api/v1/cms/sport-event-image`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetStores
		 * @request GET:/api/v1/cms/bet-stores
		 */
		getStores: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: GetBetShopResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/bet-stores`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetSeo
		 * @request GET:/api/v1/cms/seo/{type}
		 */
		getSEO: (type: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SeoResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/seo/${type}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetRegulation
		 * @request GET:/api/v1/cms/regulations/{type}
		 */
		getRegulation: (type: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SeoResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/regulations/${type}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetBetmatch
		 * @request GET:/api/v1/cms/betmatches
		 */
		getBetmatch: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BetmatchResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/cms/betmatches`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Cms
		 * @name GetCategories
		 * @request GET:/api/v1/cms/categories
		 */
		getCategories: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
				},
				void
			>({
				path: `/api/v1/cms/categories`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	Offers = {
		/**
		 * No description
		 *
		 * @tags Offers
		 * @name GetBonusList
		 * @summary to get bonus list of user
		 * @request GET:/api/v1/offers/bonus
		 * @secure
		 */
		getBonusList: (
			query: {
				/** start date for seach in dd-mm-yyyy format */
				from?: string;
				/** end date for seach in dd-mm-yyyy format */
				to?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * offset from where to start fetching records
				 * @default 0
				 */
				offset: number;
				/** which bonuses to fetch */
				status?: ('ACTIVE' | 'QUEUE' | 'HISTORY')[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BonusResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/bonus`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name ActivateBonus
		 * @summary to activate bonus
		 * @request POST:/api/v1/offers/bonus/activate/{queueId}
		 * @secure
		 */
		activateBonus: (queueId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BonusResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/bonus/activate/${queueId}`,
				method: 'POST',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name RejectBonus
		 * @summary to reject bonus
		 * @request POST:/api/v1/offers/bonus/reject/{queueId}
		 * @secure
		 */
		rejectBonus: (queueId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BonusResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/bonus/reject/${queueId}`,
				method: 'POST',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name CancelActiveBonus
		 * @summary to cancel active bonus
		 * @request DELETE:/api/v1/offers/bonus/{offerId}
		 * @secure
		 */
		cancelActiveBonus: (offerId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CancelActiveBonusResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/bonus/${offerId}`,
				method: 'DELETE',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name GetFreebetList
		 * @summary to get freebet list of user
		 * @request GET:/api/v1/offers/freebet
		 * @secure
		 */
		getFreebetList: (
			query: {
				/** start date for seach in dd-mm-yyyy format */
				from?: string;
				/** end date for seach in dd-mm-yyyy format */
				to?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * offset from where to start fetching records
				 * @default 0
				 */
				offset: number;
				/** freebet status to filter */
				status?: 'ACTIVE'[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: FreebetResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/freebet`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name GetBonusAndFreebetList
		 * @summary to get bonuses and freebets list of user
		 * @request GET:/api/v1/offers/bonus-and-freebets
		 * @secure
		 */
		getBonusAndFreebetList: (
			query: {
				/** start date for seach in dd-mm-yyyy format */
				from?: string;
				/** end date for seach in dd-mm-yyyy format */
				to?: string;
				/**
				 * no of record to fetch
				 * @default 10
				 */
				limit: number;
				/**
				 * offset from where to start fetching records
				 * @default 0
				 */
				offset: number;
				/** which bonuses to fetch */
				status?: ('ACTIVE' | 'QUEUE' | 'HISTORY')[];
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BonusAndFreebetResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/bonus-and-freebets`,
				method: 'GET',
				query: query,
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Offers
		 * @name GetCancelBonusList
		 * @summary to get potential list of bonus to cancel during withdrawal.
		 * @request GET:/api/v1/offers/cancel/withdrawal
		 * @secure
		 */
		getCancelBonusList: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CancelWithdrawalOffersResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/offers/cancel/withdrawal`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),
	};
	Promotions = {
		/**
		 * No description
		 *
		 * @tags Promotions
		 * @name GetPromotionsLeaderboard
		 * @summary Get leaders for promotion by ID, with optional query params
		 * @request GET:/api/v1/promotions/leaderboard/{id}
		 */
		getPromotionsLeaderboard: (
			id: number,
			query: {
				/** Period for filtering - undocummented feature of SBB */
				period: number;
				/** Number of results per page */
				pageSize: number;
				/** Page number for pagination */
				pageNo?: number;
			},
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PromotionLeadersResponse;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				any
			>({
				path: `/api/v1/promotions/leaderboard/${id}`,
				method: 'GET',
				query: query,
				format: 'json',
				...params,
			}),
	};
	Documents = {
		/**
		 * No description
		 *
		 * @tags Documents
		 * @name GetDocumentTypes
		 * @summary to get document types list
		 * @request GET:/api/v1/documents/types
		 */
		getDocumentTypes: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: DocumentTypesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/documents/types`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	Payments = {
		/**
		 * No description
		 *
		 * @tags Payments
		 * @name GetActiveProviders
		 * @summary to get active providers list
		 * @request GET:/api/v1/payments/providers/active
		 */
		getActiveProviders: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ActiveProvidersResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/providers/active`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name DoPayout
		 * @summary to do payout from user balance
		 * @request POST:/api/v1/payments/payout
		 * @secure
		 */
		doPayout: (data: DoPayoutBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PayoutResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/payout`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name DoDeposit
		 * @summary to do deposit to user balance
		 * @request POST:/api/v1/payments/deposit
		 * @secure
		 */
		doDeposit: (data: DoDepositBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: DepositResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/deposit`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name InitializePayment
		 * @summary to initialize payment
		 * @request POST:/api/v1/payments/initialize
		 * @secure
		 */
		initializePayment: (data: InitializePaymentBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: InitializePaymentResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/initialize`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name ValidateApplePay
		 * @summary to validate apple pay
		 * @request POST:/api/v1/payments/validate-apple-pay
		 * @secure
		 */
		validateApplePay: (params: RequestParams = {}) =>
			this.request<
				{
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
				},
				void
			>({
				path: `/api/v1/payments/validate-apple-pay`,
				method: 'POST',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name ProcessPayment
		 * @summary to process payment, final second step of payment process
		 * @request POST:/api/v1/payments/process
		 * @secure
		 */
		processPayment: (data: ProcessPaymentBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: ProcessPaymentResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/process`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name StartTransfer
		 * @summary to start transfer, it is middle step between deposit and redirect to url
		 * @request POST:/api/v1/payments/start-transfer/{transactionId}
		 * @secure
		 */
		startTransfer: (transactionId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: StartTransferResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/start-transfer/${transactionId}`,
				method: 'POST',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name GetCustomerBankDetails
		 * @summary to get customer bank details
		 * @request GET:/api/v1/payments/customer-bank-details
		 * @secure
		 */
		getCustomerBankDetails: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: CustomerBankDetailsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/customer-bank-details`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name GetBanks
		 * @summary to get list of banks of provider
		 * @request GET:/api/v1/payments/banks/{providerId}
		 */
		getBanks: (providerId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BanksResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/banks/${providerId}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name GetSavedCreditCardDetails
		 * @summary to get details of a saved credit card
		 * @request GET:/api/v1/payments/credit-card/list/{providerId}
		 * @secure
		 */
		getSavedCreditCardDetails: (providerId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SavedCreditCardDetailsResponseDto[];
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/credit-card/list/${providerId}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name GetPaymentConfig
		 * @summary to get payment configurations
		 * @request GET:/api/v1/payments/config
		 */
		getPaymentConfig: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: PaymentConfigResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/config`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name SetCreditCardVisibility
		 * @summary to set a visibility of a saved credit card
		 * @request POST:/api/v1/payments/credit-card/update
		 * @secure
		 */
		setCreditCardVisibility: (data: CreditCardVisibilityDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: boolean;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/payments/credit-card/update`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name PaymentVerify
		 * @summary to save credit card info
		 * @request POST:/api/v1/payments/payment/verify
		 * @secure
		 */
		paymentVerify: (data: PaymentVerifyBodyDto, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/payments/payment/verify`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Payments
		 * @name PaymentProviderStatus
		 * @summary see the payment provider status
		 * @request GET:/api/v1/payments/status
		 * @secure
		 */
		paymentProviderStatus: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/payments/status`,
				method: 'GET',
				secure: true,
				...params,
			}),
	};
	Misc = {
		/**
		 * No description
		 *
		 * @tags Misc
		 * @name GetGiifTypes
		 * @summary to get fetch giif types(source of income options)
		 * @request GET:/api/v1/misc/giif-types
		 */
		getGiifTypes: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/misc/giif-types`,
				method: 'GET',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Misc
		 * @name CheckVersion
		 * @summary provides information on a current allowed version of an app
		 * @request POST:/api/v1/misc/versions
		 */
		checkVersion: (data: AppVersionDto, params: RequestParams = {}) =>
			this.request<AppVersionResponseDto, void>({
				path: `/api/v1/misc/versions`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Misc
		 * @name RefreshConfig
		 * @summary force refresh middleware's config from the database.
		 * @request GET:/api/v1/misc/refresh-config/{pass}
		 */
		refreshConfig: (pass: string, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/misc/refresh-config/${pass}`,
				method: 'GET',
				...params,
			}),
	};
	Integrations = {
		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetTvbet
		 * @summary to get details of tvbet integration, use integration for card-games
		 * @request GET:/api/v1/integrations/tvbet
		 * @secure
		 */
		getTvbet: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: TvbetResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/tvbet`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetStatscoreEventsBulk
		 * @summary to get statscore event id by prematch event id
		 * @request GET:/api/v1/integrations/statscore
		 */
		getStatscoreEventsBulk: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: StatscoreResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/statscore`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetStatscoreEvent
		 * @summary to get statscore event id by prematch event id
		 * @request GET:/api/v1/integrations/statscore/{eventId}
		 */
		getStatscoreEvent: (eventId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: StatscoreEventResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/statscore/${eventId}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetBetradarLiveStream
		 * @summary to get betradar live stream url
		 * @request GET:/api/v1/integrations/betradar-live-stream/{streamId}/{streamType}/{device}
		 * @secure
		 */
		getBetradarLiveStream: (
			streamId: string,
			streamType: 'hls-manifest' | 'dash-manifest',
			device: 'Mobile' | 'Desktop',
			params: RequestParams = {},
		) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: BetradarLiveStreamResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/betradar-live-stream/${streamId}/${streamType}/${device}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetSisLiveStreamBySport
		 * @summary To get sis live stream url
		 * @request GET:/api/v1/integrations/sis-live-stream/{sportId}
		 * @secure
		 */
		getSisLiveStreamBySport: (sportId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SisLiveStreamResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/sis-live-stream/${sportId}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetSisLiveStreamByEventOrSport
		 * @summary To get sis live stream url by eventId or sportId
		 * @request GET:/api/v1/integrations/sis-live-stream/event-id/{eventId}/sport-id/{sportId}
		 * @secure
		 */
		getSisLiveStreamByEventOrSport: (eventId: string, sportId: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: SisLiveStreamResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/integrations/sis-live-stream/event-id/${eventId}/sport-id/${sportId}`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetLow6Token
		 * @request GET:/api/v1/integrations/low6
		 */
		getLow6Token: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/integrations/low6`,
				method: 'GET',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetLow6TokenForLoggedUser
		 * @request GET:/api/v1/integrations/low6logged
		 * @secure
		 */
		getLow6TokenForLoggedUser: (params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/integrations/low6logged`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name ValidateLoggedLow6Token
		 * @request GET:/api/v1/integrations/low6logged/validate
		 */
		validateLoggedLow6Token: (
			query: {
				token: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/v1/integrations/low6logged/validate`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name ValidateToken
		 * @request GET:/api/v1/integrations/low6/validate
		 */
		validateToken: (
			query: {
				token: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, void>({
				path: `/api/v1/integrations/low6/validate`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * @description Retrieve all prematch events for today and tomorrow. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetTodaysEvents
		 * @summary Get Prematch Events for 2 days
		 * @request GET:/api/v1/integrations/sportbetfan/prematch
		 */
		getTodaysEvents: (params: RequestParams = {}) =>
			this.request<EventDto[], void>({
				path: `/api/v1/integrations/sportbetfan/prematch`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve all prematch events for today and tomorrow. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetTodaysEvents2
		 * @summary Get Prematch Events for 2 days
		 * @request GET:/api/v1/integrations/marketing/prematch
		 * @originalName getTodaysEvents
		 * @duplicate
		 */
		getTodaysEvents2: (params: RequestParams = {}) =>
			this.request<EventDto[], void>({
				path: `/api/v1/integrations/marketing/prematch`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve specific prematch events by their IDs. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetEvent
		 * @summary Get Specific Prematch Events
		 * @request GET:/api/v1/integrations/sportbetfan/prematch/{eventIds}
		 */
		getEvent: (eventIds: string[], params: RequestParams = {}) =>
			this.request<EventDto, void>({
				path: `/api/v1/integrations/sportbetfan/prematch/${eventIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve specific prematch events by their IDs. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetEvent2
		 * @summary Get Specific Prematch Events
		 * @request GET:/api/v1/integrations/marketing/prematch/{eventIds}
		 * @originalName getEvent
		 * @duplicate
		 */
		getEvent2: (eventIds: string[], params: RequestParams = {}) =>
			this.request<EventDto, void>({
				path: `/api/v1/integrations/marketing/prematch/${eventIds}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve all live events currently happening. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetLiveEvents
		 * @summary Get Live Events
		 * @request GET:/api/v1/integrations/sportbetfan/live
		 */
		getLiveEvents: (params: RequestParams = {}) =>
			this.request<EventDto[], void>({
				path: `/api/v1/integrations/sportbetfan/live`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve all live events currently happening. URLs are added to the events.
		 *
		 * @tags Integrations
		 * @name GetLiveEvents2
		 * @summary Get Live Events
		 * @request GET:/api/v1/integrations/marketing/live
		 * @originalName getLiveEvents
		 * @duplicate
		 */
		getLiveEvents2: (params: RequestParams = {}) =>
			this.request<EventDto[], void>({
				path: `/api/v1/integrations/marketing/live`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * @description Retrieve all available event categories.
		 *
		 * @tags Integrations
		 * @name GetCategories
		 * @summary Get Event Categories
		 * @request GET:/api/v1/integrations/sportbetfan/categories
		 */
		getCategories: (params: RequestParams = {}) =>
			this.request<EventCategoryDto[], void>({
				path: `/api/v1/integrations/sportbetfan/categories`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetBestsellers
		 * @request GET:/api/v1/integrations/sportbetfan/bestsellers
		 */
		getBestsellers: (params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/integrations/sportbetfan/bestsellers`,
				method: 'GET',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Integrations
		 * @name GetPlayersCoupon
		 * @request GET:/api/v1/integrations/sportbetfan/kupon-kibica/{id}
		 */
		getPlayersCoupon: (id: string, params: RequestParams = {}) =>
			this.request<void, void>({
				path: `/api/v1/integrations/sportbetfan/kupon-kibica/${id}`,
				method: 'GET',
				...params,
			}),
	};
	Virtuals = {
		/**
		 * No description
		 *
		 * @tags Virtuals
		 * @name GetRunningSports
		 * @summary to get list of running sports
		 * @request GET:/api/v1/virtuals/running-sports
		 */
		getRunningSports: (params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: VirtualRunningSportsResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/virtuals/running-sports`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Virtuals
		 * @name GenerateEventOutcomes
		 * @summary to generated outcome of virtual sport
		 * @request POST:/api/v1/virtuals/generate-outcomes
		 */
		generateEventOutcomes: (data: GenerateUofOutcomesBodyDto, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: GenerateUofOutcomesResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				void
			>({
				path: `/api/v1/virtuals/generate-outcomes`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				format: 'json',
				...params,
			}),
	};
	KuponKibica = {
		/**
		 * No description
		 *
		 * @tags Kupon Kibica
		 * @name FindOne
		 * @request GET:/api/v1/kupon-kibica/{id}
		 */
		findOne: (id: string, params: RequestParams = {}) =>
			this.request<
				| {
						/** @example false */
						isError: false;
						/** @example 200 */
						code: number;
						data: KuponKibicaResponseDto;
				  }
				| {
						/** @example true */
						isError: true;
						/** @example 500 */
						code: number;
						message: string;
				  },
				any
			>({
				path: `/api/v1/kupon-kibica/${id}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Kupon Kibica
		 * @name FindAndShare
		 * @request GET:/api/v1/kupon-kibica/{id}/share/{name}
		 */
		findAndShare: (id: string, name: string, params: RequestParams = {}) =>
			this.request<
				{
					/** @example true */
					isError: true;
					/** @example 500 */
					code: number;
					message: string;
				},
				any
			>({
				path: `/api/v1/kupon-kibica/${id}/share/${name}`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	PlayerProfiles = {
		/**
		 * @description Fetch the available avatars and their categories for the user based on their customerId.
		 *
		 * @tags Player's Profile
		 * @name GetAvailableAvatars
		 * @summary Get available avatars and categories for the user.
		 * @request GET:/api/v1/player-profiles/avatars
		 * @secure
		 */
		getAvailableAvatars: (params: RequestParams = {}) =>
			this.request<AvatarsResponse, void>({
				path: `/api/v1/player-profiles/avatars`,
				method: 'GET',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * @description Save the selected avatar (identified by avatarId) to the user's profile.
		 *
		 * @tags Player's Profile
		 * @name SavePlayerAvatar
		 * @summary Save player avatar to the profile.
		 * @request PUT:/api/v1/player-profiles/avatars/{avatarId}
		 * @secure
		 */
		savePlayerAvatar: (avatarId: number, params: RequestParams = {}) =>
			this.request<SaveProfileResponseDto, void>({
				path: `/api/v1/player-profiles/avatars/${avatarId}`,
				method: 'PUT',
				secure: true,
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name GetAvailableMedals
		 * @request GET:/api/v1/player-profiles/medals
		 * @secure
		 */
		getAvailableMedals: (params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles/medals`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name ClaimMedal
		 * @request PUT:/api/v1/player-profiles/medals/{medalId}
		 * @secure
		 */
		claimMedal: (medalId: number, params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles/medals/${medalId}`,
				method: 'PUT',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name SavePlayersProfile
		 * @request PUT:/api/v1/player-profiles
		 * @secure
		 */
		savePlayersProfile: (data: PutPlayerProfileDto, params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles`,
				method: 'PUT',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name GetObserved
		 * @request GET:/api/v1/player-profiles/observed
		 * @secure
		 */
		getObserved: (params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles/observed`,
				method: 'GET',
				secure: true,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name AddToObserved
		 * @request POST:/api/v1/player-profiles/observed
		 * @secure
		 */
		addToObserved: (data: ObservedDto, params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles/observed`,
				method: 'POST',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags Player's Profile
		 * @name RemoveFromObserved
		 * @request DELETE:/api/v1/player-profiles/observed
		 * @secure
		 */
		removeFromObserved: (data: ObservedDto, params: RequestParams = {}) =>
			this.request<any, void>({
				path: `/api/v1/player-profiles/observed`,
				method: 'DELETE',
				body: data,
				secure: true,
				type: ContentType.Json,
				...params,
			}),
	};
	DailyChallenge = {
		/**
		 * @description Retrieve all active daily challenges for the current day.
		 *
		 * @tags Daily Challenge
		 * @name GetTodaysChallenges
		 * @summary Get Today's Challenges
		 * @request GET:/api/v1/daily-challenge
		 */
		getTodaysChallenges: (params: RequestParams = {}) =>
			this.request<DailyChallengeModel[], void>({
				path: `/api/v1/daily-challenge`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	Analytics = {
		/**
		 * No description
		 *
		 * @tags Analytics
		 * @name CollectData
		 * @request POST:/api/v1/analytics/betslips
		 */
		collectData: (data: AnalyticsObject, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/analytics/betslips`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				...params,
			}),
	};
	Authologic = {
		/**
		 * No description
		 *
		 * @name InitializeAuthologic
		 * @request POST:/api/v1/authologic/initialize/{conversationType}
		 */
		initializeAuthologic: (conversationType: string, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/authologic/initialize/${conversationType}`,
				method: 'POST',
				...params,
			}),

		/**
		 * No description
		 *
		 * @name AcceptCallback
		 * @request POST:/api/v1/authologic/callback/{conversationType}/{authologicConversationId}
		 */
		acceptCallback: (conversationType: string, authologicConversationId: string, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/authologic/callback/${conversationType}/${authologicConversationId}`,
				method: 'POST',
				...params,
			}),

		/**
		 * No description
		 *
		 * @name CheckConversationResult
		 * @request GET:/api/v1/authologic/check/{conversationType}/{authologicConversationId}
		 */
		checkConversationResult: (conversationType: string, authologicConversationId: string, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/authologic/check/${conversationType}/${authologicConversationId}`,
				method: 'GET',
				...params,
			}),

		/**
		 * No description
		 *
		 * @name FinishRegistration
		 * @request POST:/api/v1/authologic/finish/registrationMojeId/{authologicConversationId}
		 */
		finishRegistration: (authologicConversationId: string, data: FinishRegistrationDto, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/authologic/finish/registrationMojeId/${authologicConversationId}`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				...params,
			}),

		/**
		 * No description
		 *
		 * @name FinishVerification
		 * @request POST:/api/v1/authologic/finish/verificationMojeId/{authologicConversationId}
		 */
		finishVerification: (authologicConversationId: string, data: FinishVerificationDto, params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/authologic/finish/verificationMojeId/${authologicConversationId}`,
				method: 'POST',
				body: data,
				type: ContentType.Json,
				...params,
			}),
	};
	EventFilters = {
		/**
		 * No description
		 *
		 * @tags EventFilters
		 * @name GetAvailableFiltersForCategory
		 * @summary Returns available event filters by category ID
		 * @request GET:/api/v1/event-filters/category/{categoryId}
		 */
		getAvailableFiltersForCategory: (categoryId: number, params: RequestParams = {}) =>
			this.request<AvailableFiltersResponse, void>({
				path: `/api/v1/event-filters/category/${categoryId}`,
				method: 'GET',
				format: 'json',
				...params,
			}),

		/**
		 * No description
		 *
		 * @tags EventFilters
		 * @name GetAvailableFiltersForEsport
		 * @summary Returns available event filters by e-sports
		 * @request GET:/api/v1/event-filters/esport
		 */
		getAvailableFiltersForEsport: (params: RequestParams = {}) =>
			this.request<AvailableFiltersResponse, void>({
				path: `/api/v1/event-filters/esport`,
				method: 'GET',
				format: 'json',
				...params,
			}),
	};
	LoadTest = {
		/**
		 * No description
		 *
		 * @name CpuIntensive
		 * @request GET:/api/v1/load-test/cpu-intensive
		 */
		cpuIntensive: (
			query: {
				duration: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, any>({
				path: `/api/v1/load-test/cpu-intensive`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * No description
		 *
		 * @name MemoryIntensive
		 * @request GET:/api/v1/load-test/memory-intensive
		 */
		memoryIntensive: (
			query: {
				size: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, any>({
				path: `/api/v1/load-test/memory-intensive`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * No description
		 *
		 * @name MixedLoad
		 * @request POST:/api/v1/load-test/mixed-load
		 */
		mixedLoad: (params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/load-test/mixed-load`,
				method: 'POST',
				...params,
			}),

		/**
		 * No description
		 *
		 * @name Fibonacci
		 * @request GET:/api/v1/load-test/fibonacci
		 */
		fibonacci: (
			query: {
				n: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, any>({
				path: `/api/v1/load-test/fibonacci`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * No description
		 *
		 * @name HashIntensive
		 * @request GET:/api/v1/load-test/hash-intensive
		 */
		hashIntensive: (
			query: {
				iterations: string;
			},
			params: RequestParams = {},
		) =>
			this.request<void, any>({
				path: `/api/v1/load-test/hash-intensive`,
				method: 'GET',
				query: query,
				...params,
			}),

		/**
		 * No description
		 *
		 * @name GetHealth
		 * @request GET:/api/v1/load-test/health
		 */
		getHealth: (params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/load-test/health`,
				method: 'GET',
				...params,
			}),
	};
	Ping = {
		/**
		 * No description
		 *
		 * @name PingV1
		 * @request GET:/api/v1/ping
		 */
		pingV1: (params: RequestParams = {}) =>
			this.request<void, any>({
				path: `/api/v1/ping`,
				method: 'GET',
				...params,
			}),
	};
}
